import { useState, useEffect, useRef, useCallback } from 'react';
import { Link } from 'react-router-dom';
import {

  Loader2, ArrowRight, MapPin, AlertTriangle, Download, Search, Info,
  ChevronDown, Crosshair, Building2, Waves, Wind, TrendingUp, TrendingDown,
  Minus, ShieldAlert, History, CheckCircle2, CloudDownload, RefreshCw,
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent } from '../components/ui/card';
import { SEO } from '../components/SEO';

import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { getApiBase } from '../lib/apiBase';

// Fix Leaflet's default marker icon (CRA bundles assets oddly)
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png',
  iconUrl:       'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png',
  shadowUrl:     'https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png',
});

const API_URL = getApiBase();

// Class colour map (matches backend CLASS_COLOURS)
const CLASS_BG = {
  C1: '#10B981', C2: '#84CC16', C3: '#F59E0B',
  C4: '#EF4444', C5: '#B91C1C', CX: '#7F1D1D',
};
const CLASS_LABEL = {
  C1: 'Very low corrosivity',
  C2: 'Low corrosivity',
  C3: 'Medium corrosivity',
  C4: 'High corrosivity',
  C5: 'Very high corrosivity',
  CX: 'Extreme corrosivity',
};

// Human-friendly labels — DO NOT surface raw tags in the main UI.
const SO2_ATMOSPHERE_LABELS = {
  rural: 'Low-sulfur atmosphere',
  urban: 'Urban sulfur levels',
  industrial: 'Industrial sulfur levels',
  highly_polluted_industrial: 'Highly polluted industrial',
};
const MARINE_CONTEXT_LABELS = {
  inland: 'Inland',
  maritime: 'Maritime',
  strong_marine: 'Strong marine',
};
const SETTLEMENT_LABELS = {
  city: 'City',
  town: 'Town',
  rural_area: 'Rural area',
  industrial_site: 'Industrial site',
};
// Precision badge labels are now driven by `metadata.resolved_precision` from
// the backend (city_centroid / address_point / exact_point / representative_place),
// not the legacy heuristic `precision_level` (low / medium / high). This is
// authoritative — the backend knows whether the user typed coordinates, picked
// from autocomplete, or supplied a street address.
const PRECISION_META_BY_RESOLVED = {
  exact_point:           { label: 'Coordinates — highest precision', tone: 'emerald' },
  address_point:         { label: 'Address — higher precision',      tone: 'emerald' },
  representative_place:  { label: 'Place — representative',           tone: 'amber'   },
  city_centroid:         { label: 'City — approximate',               tone: 'amber'   },
};

// Legacy fallback for older cached responses without the new metadata.
const PRECISION_META = {
  low:    { label: 'City — approximate',               tone: 'amber'   },
  medium: { label: 'Address — improved',               tone: 'emerald' },
  high:   { label: 'Coordinates — highest precision',  tone: 'emerald' },
};

// Coordinate string detector ("51.5, -0.12" / "51.5,-0.12" / "51.5 -0.12")
const COORD_RE = /^\s*(-?\d{1,3}(?:\.\d+)?)\s*[,\s]\s*(-?\d{1,3}(?:\.\d+)?)\s*$/;

// ── v10: CAMS-enhanced UI constants ────────────────────────────────────
// Four-step indeterminate stepper. The active highlight cycles on a timer —
// this is an ACTIVITY INDICATOR, not measured backend progress. No percentages,
// no "step 3 of 4 complete" wording.
const LOADING_STEPS = [
  { key: 'resolve',   label: 'Resolving location' },
  { key: 'cams',      label: 'Retrieving atmospheric data' },
  { key: 'classify',  label: 'Applying ISO 9223 classification' },
  { key: 'report',    label: 'Generating report' },
];
const STEP_INTERVAL_MS   = 2500;
const REASSURANCE_MS     = 9000;

// Trend badge metadata. `classification_changed` is shown separately as an
// amber chip alongside the direction.
const TREND_META = {
  improving: { label: 'Improving',  tone: 'emerald', Icon: TrendingDown },
  worsening: { label: 'Worsening',  tone: 'red',     Icon: TrendingUp   },
  stable:    { label: 'Stable',     tone: 'neutral', Icon: Minus        },
  unknown:   { label: 'No trend',   tone: 'neutral', Icon: Minus        },
};

// ── v13: derive the trend badge purely from steel class + r_corr ─────
// Source of truth (per FRONTEND_CONTRACT.md):
//   current.metals.steel.{class, r_corr}
//   historical.metals.steel.{class, r_corr}
// Rules:
//   1. If steel classes differ → direction follows class order.
//      A worse class always means "worsening" — never "improving",
//      regardless of any ΔPd / ΔSd / ΔRH / ΔT chip.
//   2. If steel classes match → fall back to r_corr ratio:
//        ratio ≤ 0.9 → improving
//        ratio ≥ 1.1 → worsening
//        else        → stable
//   3. If historical r_corr is 0 or invalid → "none" (hide the badge).
// `classificationChanged` is true iff steel classes differ.
const CLASS_ORDER = ['C1', 'C2', 'C3', 'C4', 'C5', 'CX'];
const classIndex = (c) => CLASS_ORDER.indexOf(c);

const deriveTrend = (currentSteel, historicalSteel) => {
  if (!currentSteel || !historicalSteel) return null;
  const cc = currentSteel.class;
  const hc = historicalSteel.class;
  if (!cc || !hc) return null;

  const ci = classIndex(cc);
  const hi = classIndex(hc);
  if (ci === -1 || hi === -1) return null;

  const classificationChanged = cc !== hc;

  let direction;
  if (cc !== hc) {
    direction = ci > hi ? 'worsening' : 'improving';
  } else {
    const cr = Number(currentSteel.r_corr);
    const hr = Number(historicalSteel.r_corr);
    if (!Number.isFinite(hr) || hr <= 0 || !Number.isFinite(cr)) {
      return null;  // hide
    }
    const ratio = cr / hr;
    if (ratio <= 0.9)      direction = 'improving';
    else if (ratio >= 1.1) direction = 'worsening';
    else                   direction = 'stable';
  }
  return { direction, classification_changed: classificationChanged };
};
const TREND_TONE_CLS = {
  emerald: 'bg-emerald-50 border-emerald-200 text-emerald-800',
  red:     'bg-red-50 border-red-200 text-red-800',
  amber:   'bg-amber-50 border-amber-200 text-amber-800',
  neutral: 'bg-neutral-100 border-neutral-200 text-neutral-700',
};

// Heuristic: did the backend fall back to placeholder / proxy data for
// atmospheric SO₂ or chloride? If so, we surface an informational banner.
const hasFallbackAtmData = (res) => {
  if (!res) return false;
  const cur = res.current || {};
  const tags = [
    res.so2_source,
    res.chloride_source,
    cur.data_source,
  ].filter(Boolean).join('|').toLowerCase();
  return /placeholder|distance_proxy|estimated|fallback/.test(tags);
};

// Small helper to render an indeterminate 4-step loading stepper.
// `activeIdx` is driven by a timer in the parent component.
const LoadingStepper = ({ activeIdx, showReassurance }) => (
  <div
    className="mt-5 rounded-xl border border-emerald-100 bg-emerald-50/40 p-5 space-y-4"
    role="status"
    aria-live="polite"
    data-testid="corrosivity-loading-stepper"
  >
    <div className="flex items-center gap-2">
      <Loader2 className="w-4 h-4 text-emerald-700 animate-spin" />
      <span className="text-sm font-semibold text-emerald-900">
        Retrieving atmospheric data and building ISO&nbsp;9223 assessment…
      </span>
    </div>
    <ol className="space-y-2">
      {LOADING_STEPS.map((s, i) => {
        const isActive = i === activeIdx;
        const isDone   = i < activeIdx;
        return (
          <li
            key={s.key}
            className="flex items-center gap-3"
            data-testid={`corrosivity-loading-step-${s.key}`}
            data-active={isActive ? 'true' : 'false'}
          >
            <span
              className={`w-5 h-5 rounded-full flex items-center justify-center border text-[10px] font-bold flex-shrink-0 ${
                isDone
                  ? 'bg-emerald-600 border-emerald-600 text-white'
                  : isActive
                    ? 'bg-white border-emerald-500 text-emerald-700 animate-pulse'
                    : 'bg-white border-neutral-300 text-neutral-400'
              }`}
            >
              {isDone ? <CheckCircle2 className="w-3 h-3" /> : i + 1}
            </span>
            <span
              className={`text-sm ${
                isActive
                  ? 'text-neutral-900 font-medium'
                  : isDone
                    ? 'text-neutral-600'
                    : 'text-neutral-500'
              }`}
            >
              {s.label}
            </span>
          </li>
        );
      })}
    </ol>
    <p className="text-xs text-neutral-600 leading-relaxed">
      First-time requests for a new region can take longer because atmospheric
      data may need to be retrieved and cached.
    </p>
    {showReassurance && (
      <p
        className="text-xs text-emerald-800 font-medium leading-relaxed"
        data-testid="corrosivity-loading-reassurance"
      >
        Still working — this can take around 60–90&nbsp;seconds for uncached
        locations.
      </p>
    )}
  </div>
);

// Trend pill used on the headline card, next to the ISO class pill.
const TrendBadge = ({ trend }) => {
  if (!trend || !trend.direction) return null;
  const meta = TREND_META[trend.direction] || TREND_META.unknown;
  const Icon = meta.Icon;
  return (
    <div className="flex items-center gap-2 flex-wrap" data-testid="corrosivity-trend-badge">
      <span
        className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold border ${TREND_TONE_CLS[meta.tone]}`}
        data-testid={`corrosivity-trend-direction-${trend.direction}`}
      >
        <Icon className="w-3.5 h-3.5" />
        {meta.label}
      </span>
      {trend.classification_changed ? (
        <span
          className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold border ${TREND_TONE_CLS.amber}`}
          data-testid="corrosivity-trend-class-changed"
        >
          <ShieldAlert className="w-3.5 h-3.5" />
          Classification changed
        </span>
      ) : (
        <span
          className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold border ${TREND_TONE_CLS.neutral}`}
          data-testid="corrosivity-trend-class-stable"
        >
          ISO class consistent since 2013
        </span>
      )}
    </div>
  );
};

// Small δ-chip used in the historical comparison header.
const DeltaChip = ({ label, value, unit, higherIsWorse = true }) => {
  if (value === null || value === undefined || Number.isNaN(value)) return null;
  const sign = value > 0 ? '+' : '';
  // Colour semantics: for Pd/Sd, higher = worse. For RH/T we keep neutral.
  let tone = 'neutral';
  if (higherIsWorse) {
    if (value > 5)       tone = 'red';
    else if (value < -5) tone = 'emerald';
  }
  return (
    <span
      className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-semibold border ${TREND_TONE_CLS[tone]}`}
      data-testid={`corrosivity-delta-${label.toLowerCase().replace(/\s+/g, '-')}`}
    >
      {label} {sign}{value.toFixed(1)}{unit}
    </span>
  );
};

// One snapshot card (Current or Historical 2013).
const SnapshotCard = ({ title, snapshot, variant = 'current' }) => {
  const cls = snapshot?.corrosivity_class;
  const year = snapshot?.year;
  const borderTone = variant === 'historical'
    ? 'border-neutral-200 bg-neutral-50/60'
    : 'border-emerald-200 bg-white';
  // v16.1 — explain the year label so users don't wonder why data stops at 2023.
  const yearSubtitle = variant === 'historical'
    ? '10-year comparison baseline'
    : 'Latest available CAMS EAC4 reanalysis year';
  return (
    <div
      className={`rounded-xl border p-4 ${borderTone}`}
      data-testid={`corrosivity-snapshot-${variant}`}
    >
      <div className="flex items-start justify-between mb-3 gap-3">
        <div className="text-[11px] uppercase tracking-wider text-neutral-500 font-semibold">
          {title}
        </div>
        {year && (
          <div className="text-right" data-testid={`corrosivity-snapshot-year-${variant}`}>
            <div className="text-[11px] font-mono text-neutral-700 font-semibold">{year}</div>
            <div className="text-[10px] text-neutral-500 mt-0.5 leading-tight max-w-[180px]">
              {yearSubtitle}
            </div>
          </div>
        )}
      </div>
      <div className="flex items-center gap-3 mb-3">
        {cls ? (
          <ClassPill cls={cls} />
        ) : (
          <span className="text-xs text-neutral-400">—</span>
        )}
        {cls && (
          <span className="text-sm text-neutral-700">{CLASS_LABEL[cls]}</span>
        )}
      </div>
      <dl className="grid grid-cols-2 gap-x-4 gap-y-2 text-xs">
        <div>
          <dt className="text-neutral-500">SO₂ dep. (Pd)</dt>
          <dd className="text-neutral-900 font-semibold tabular-nums">
            {snapshot?.Pd_mg_m2_d != null ? snapshot.Pd_mg_m2_d.toFixed(2) : '—'}
            <span className="text-neutral-500 font-normal ml-1">mg/m²·d</span>
          </dd>
        </div>
        <div>
          <dt className="text-neutral-500">Chloride (Sd)</dt>
          <dd className="text-neutral-900 font-semibold tabular-nums">
            {snapshot?.Sd_mg_m2_d != null ? snapshot.Sd_mg_m2_d.toFixed(2) : '—'}
            <span className="text-neutral-500 font-normal ml-1">mg/m²·d</span>
          </dd>
        </div>
        <div>
          <dt className="text-neutral-500">Rel. humidity</dt>
          <dd className="text-neutral-900 font-semibold tabular-nums">
            {snapshot?.RH_pct != null ? snapshot.RH_pct.toFixed(1) : '—'}
            <span className="text-neutral-500 font-normal ml-1">%</span>
          </dd>
        </div>
        <div>
          <dt className="text-neutral-500">Temperature</dt>
          <dd className="text-neutral-900 font-semibold tabular-nums">
            {snapshot?.T_C != null ? snapshot.T_C.toFixed(1) : '—'}
            <span className="text-neutral-500 font-normal ml-1">°C</span>
          </dd>
        </div>
      </dl>
    </div>
  );
};

// Placeholder card shown when the backend couldn't retrieve the 2013 snapshot.
// Styled as informational, not alarming — the current classification remains valid.
const HistoricalUnavailableCard = () => (
  <div
    className="rounded-xl border border-neutral-200 bg-neutral-50 p-4 flex flex-col justify-center items-start"
    data-testid="corrosivity-historical-unavailable"
  >
    <div className="flex items-center gap-2 mb-2">
      <History className="w-4 h-4 text-neutral-500" />
      <div className="text-[11px] uppercase tracking-wider text-neutral-500 font-semibold">
        Historical (2013)
      </div>
    </div>
    <div className="text-sm font-medium text-neutral-800 mb-1">
      Historical snapshot unavailable for this location.
    </div>
    <p className="text-xs text-neutral-600 leading-relaxed">
      We could not retrieve the 2013 CAMS-backed comparison for this grid cell.
      The current ISO&nbsp;9223 classification is still valid; only the
      historical comparison is unavailable.
    </p>
  </div>
);

const ClassPill = ({ cls, size = 'md' }) => {
  const bg = CLASS_BG[cls] || '#6B7280';
  const sz = size === 'lg' ? 'text-3xl px-5 py-2' : 'text-sm px-3 py-1';
  return (
    <span
      className={`inline-flex items-center gap-2 rounded-full font-bold text-white ${sz}`}
      style={{ background: bg }}
      data-testid={`class-pill-${cls}`}
    >
      {cls}
    </span>
  );
};

const SummaryRow = ({ icon: Icon, label, value, testId }) => (
  <div className="flex items-center gap-3 py-2.5 border-b border-neutral-100 last:border-0" data-testid={testId}>
    <div className="w-8 h-8 rounded-full bg-emerald-50 flex items-center justify-center flex-shrink-0">
      <Icon className="w-4 h-4 text-emerald-700" />
    </div>
    <div className="flex-1 min-w-0">
      <div className="text-[11px] uppercase tracking-wider text-neutral-500">{label}</div>
      <div className="text-sm font-semibold text-neutral-900 truncate">{value || '—'}</div>
    </div>
  </div>
);

const Stat = ({ label, value, unit }) => (
  <div className="flex flex-col gap-0.5">
    <div className="text-[11px] uppercase tracking-wider text-neutral-500">{label}</div>
    <div className="text-base font-semibold text-neutral-900">
      {value}
      {unit && <span className="text-neutral-500 font-normal text-sm ml-1">{unit}</span>}
    </div>
  </div>
);

// Vanilla-Leaflet map panel — sidesteps react-leaflet's StrictMode double-mount bug.
// The circle here is an UNCERTAINTY INDICATOR (not a corrosivity zone or marine
// exposure zone). Its radius is driven by distance_uncertainty_km from the
// backend, capped so it doesn't dominate the map, and hidden entirely for
// exact-coordinate inputs where precision is already high.
const MapPanel = ({ lat, lon, displayName, primary, classLabel, steel, uncertaintyKm, precisionLevel, distanceToSeaKm }) => {
  const containerRef = useRef(null);
  const mapRef       = useRef(null);

  useEffect(() => {
    if (!containerRef.current) return;
    if (mapRef.current) {
      mapRef.current.remove();
      mapRef.current = null;
    }

    // Zoom closer for higher-precision inputs so the pin reads naturally
    const zoom = precisionLevel === 'high'
      ? 13
      : precisionLevel === 'medium'
        ? 11
        : 10;

    const map = L.map(containerRef.current, { scrollWheelZoom: false }).setView([lat, lon], zoom);
    L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
      attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>',
      maxZoom: 18,
    }).addTo(map);

    // Draw a subtle uncertainty halo ONLY when the location isn't exact.
    // Cap at 15 km so even a 20 km city-level uncertainty still reads as a halo
    // rather than an overlay covering the whole map.
    const uncertaintyMeters = Math.min(
      Math.max(Number(uncertaintyKm) || 0, 0),
      15,
    ) * 1000;

    const isPrecise = precisionLevel === 'high' || precisionLevel === 'medium';
    const distanceNote = Number.isFinite(distanceToSeaKm)
      ? distanceToSeaKm < 1
        ? `Analyzed: ${(distanceToSeaKm * 1000).toFixed(0)} m from coastline`
        : `Analyzed: ${distanceToSeaKm.toFixed(2)} km from coastline`
      : '';

    if (!isPrecise && uncertaintyMeters > 0) {
      // City-name queries: render a labelled halo (no pin) so the user can
      // see the area the model is *representing*, not a misleading point.
      // The pin is suppressed because Nominatim's city centroid is inland
      // by hundreds of metres → users assume the analysis is anchored on
      // that pin, but the math is actually a representative-distance result.
      const halo = L.circle([lat, lon], {
        radius: uncertaintyMeters,
        color: '#64748B',
        weight: 1,
        opacity: 0.65,
        dashArray: '6 4',
        fillColor: '#64748B',
        fillOpacity: 0.10,
        interactive: true,
      }).addTo(map);
      halo.bindPopup(`
        <div style="font-size:13px;">
          <div style="font-weight:600;margin-bottom:4px;">
            ${(displayName || '').split(',').slice(0, 2).join(',')}
          </div>
          <div>Class <strong>${primary}</strong> — ${classLabel}</div>
          <div>Steel: ${steel?.r_corr?.toFixed(2)} ${steel?.r_corr_unit || ''}</div>
          <div style="margin-top:4px;color:#64748B;font-size:11px;">
            City-name query · representative ~${uncertaintyKm?.toFixed?.(1) ?? '?'} km area
          </div>
          ${distanceNote ? `<div style="color:#64748B;font-size:11px;">${distanceNote}</div>` : ''}
          <div style="margin-top:4px;color:#94A3B8;font-size:11px;font-style:italic;">
            For a site-specific result, enter a full street address or exact coordinates.
          </div>
        </div>
      `);
      mapRef.current = map;
      return;
    }

    // Precise input (exact coords or address-level) — pin is meaningful.
    const marker = L.marker([lat, lon]).addTo(map);
    const precisionNote = precisionLevel === 'high'
      ? 'Exact coordinates'
      : `Address-level · ~${uncertaintyKm?.toFixed?.(1) ?? '?'} km uncertainty`;
    marker.bindPopup(`
      <div style="font-size:13px;">
        <div style="font-weight:600;margin-bottom:4px;">
          ${(displayName || '').split(',').slice(0, 2).join(',')}
        </div>
        <div>Class <strong>${primary}</strong> — ${classLabel}</div>
        <div>Steel: ${steel?.r_corr?.toFixed(2)} ${steel?.r_corr_unit || ''}</div>
        <div style="margin-top:4px;color:#64748B;font-size:11px;">${precisionNote}</div>
        ${distanceNote ? `<div style="color:#64748B;font-size:11px;">${distanceNote}</div>` : ''}
      </div>
    `);
    mapRef.current = map;
    return () => {
      if (mapRef.current) { mapRef.current.remove(); mapRef.current = null; }
    };
  }, [lat, lon, primary, displayName, classLabel, steel, uncertaintyKm, precisionLevel, distanceToSeaKm]);

  return <div ref={containerRef} className="h-80 w-full" data-testid="corrosivity-map" />;
};

// ── v11: pending CAMS download state ─────────────────────────────────
// ── v11: pending CAMS download state ─────────────────────────────────
// Shown when the backend returns `status === 'pending_cams_download'`.
// This is an EXPECTED backend state (first-time uncached grid cell) — NOT
// an error. Copy stays informational and suggests "Check again" in ~3 min.
//
// v22: polished pending card —
//   • indeterminate marquee bar so the user has a visual signal that work is
//     happening on the server (CAMS reanalysis can take 3–10 min from a cold
//     queue).
//   • clear "queued" framing + worst-case time disclosure so users don't
//     assume the page is broken when it goes past 5 min.
//   • CTA to scroll back to the map (where the warming yellow marker is
//     visible) so users have something useful to see while they wait.
//   • optional auto-poll: if `onRetry` is provided we re-check every 30s
//     so the result lands by itself when CAMS finishes.
const PendingCamsCard = ({ result, onRetry, retrying, etaRemaining, onScrollToMap }) => {
  const eta = result?.cams_eta_seconds;
  // v22: light-touch auto-poll. Re-check every 30s while the card is mounted
  // so the result transitions to "success" without the user clicking anything.
  // Stops automatically once the parent unmounts this card (i.e. result.status
  // changes or user navigates away).
  useEffect(() => {
    if (!onRetry) return undefined;
    const id = setInterval(() => {
      if (!retrying) onRetry();
    }, 30000);
    return () => clearInterval(id);
  }, [onRetry, retrying]);

  return (
    <Card className="border-sky-200 bg-sky-50/40" data-testid="corrosivity-pending-card">
      <CardContent className="p-6 space-y-4">
        <div className="flex items-start gap-3">
          <div className="w-10 h-10 rounded-full bg-sky-100 flex items-center justify-center flex-shrink-0">
            <CloudDownload className="w-5 h-5 text-sky-700" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-[11px] uppercase tracking-[0.2em] text-sky-700 font-semibold">
              CAMS job queued
            </div>
            <div className="text-base font-semibold text-neutral-900 mt-1">
              {result?.resolved_name || 'This location'}
            </div>
          </div>
        </div>

        {/* Indeterminate marquee progress bar — visual reassurance that the
            server is working. Pure CSS (Tailwind keyframes), no JS timer. */}
        <div
          className="relative h-1.5 w-full overflow-hidden rounded-full bg-sky-100"
          data-testid="corrosivity-pending-progress"
          aria-label="CAMS download in progress"
          role="progressbar"
        >
          <div className="absolute inset-y-0 w-1/3 rounded-full bg-sky-500/90 animate-[marquee_1.6s_ease-in-out_infinite]" />
        </div>

        <p className="text-sm text-neutral-700 leading-relaxed">
          Your job has been <strong>accepted and queued</strong> with the
          Copernicus CAMS reanalysis service. Downloads typically take{' '}
          <strong>3–5 minutes</strong>, occasionally up to <strong>10 minutes</strong>{' '}
          when their queue is busy. We'll re-check every 30 seconds and load
          your result automatically as soon as it's ready.
        </p>

        <div className="rounded-md border border-sky-200/70 bg-white/70 p-3 text-xs text-sky-900">
          <div className="font-semibold mb-1 flex items-center gap-1.5">
            <MapPin className="w-3.5 h-3.5" /> While you wait
          </div>
          Scroll back to the <strong>Cached CAMS Areas</strong> map — you'll
          see your selected location with a <strong>yellow "warming"</strong>{' '}
          marker. It turns green when the data lands, and the result here will
          refresh automatically.
        </div>

        {(eta || etaRemaining != null) && (
          <div className="text-xs text-neutral-500 flex items-center gap-2" data-testid="corrosivity-pending-eta">
            <Info className="w-3.5 h-3.5" />
            <span>
              Estimated time remaining:{' '}
              <strong>
                {etaRemaining != null
                  ? `~${Math.max(0, Math.ceil(etaRemaining / 60))} min`
                  : `~${Math.ceil((eta || 180) / 60)} min`}
              </strong>
            </span>
          </div>
        )}

        <div className="flex flex-wrap items-center gap-3 pt-2 border-t border-sky-100">
          <Button
            onClick={onRetry}
            disabled={retrying}
            className="h-10 px-5 bg-sky-600 hover:bg-sky-700 text-white"
            data-testid="corrosivity-pending-retry"
          >
            {retrying ? (
              <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Checking…</>
            ) : (
              <><RefreshCw className="w-4 h-4 mr-2" /> Check now</>
            )}
          </Button>
          {onScrollToMap && (
            <Button
              onClick={onScrollToMap}
              variant="outline"
              className="h-10 px-4 border-sky-300 text-sky-800 hover:bg-sky-50"
              data-testid="corrosivity-pending-scroll-to-map"
            >
              <MapPin className="w-4 h-4 mr-2" /> See on map
            </Button>
          )}
          <span className="text-xs text-neutral-500">
            Once cached, the same neighbourhood loads instantly for everyone.
          </span>
        </div>
      </CardContent>
    </Card>
  );
};

// ── v15: offshore marine result state ────────────────────────────────
// Shown when the backend returns `status === 'offshore'`. The backend
// supplies a valid corrosivity class (C5 or CX) + r_corr based on the
// dedicated offshore marine rule — NOT the standard inland ISO 9223
// dose-response. This card displays the result but visually signals that
// it sits outside the normal classification path (different accent colour,
// prominent warning, no trend comparison, no per-metal table).
const OffshoreResultCard = ({ result, downloading, onDownloadPdf }) => {
  const cls = result?.corrosivity_class || 'C5';
  const r   = result?.r_corr;
  const t   = result?.temperature;
  const rh  = result?.humidity;
  const lat = result?.latitude;
  const lon = result?.longitude;
  const notes = result?.metadata?.calibration_notes || [];
  return (
    <Card className="border-cyan-300 bg-cyan-50/50" data-testid="corrosivity-offshore-card">
      <CardContent className="p-6 space-y-5">
        <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4">
          <div className="flex items-start gap-3 flex-1 min-w-0">
            <div className="w-11 h-11 rounded-full bg-cyan-100 flex items-center justify-center flex-shrink-0">
              <Waves className="w-5 h-5 text-cyan-700" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-[11px] uppercase tracking-[0.2em] text-cyan-700 font-semibold">
                Offshore marine classification
              </div>
              <div className="mt-1 flex flex-wrap items-baseline gap-3">
                <ClassPill cls={cls} size="lg" />
                <span className="text-lg text-neutral-800 font-medium">
                  {CLASS_LABEL[cls]}
                </span>
              </div>
              <div className="mt-2 text-sm text-neutral-700">
                Carbon steel · <strong data-testid="corrosivity-offshore-r-corr">{r?.toFixed(2)}</strong>
                <span className="text-neutral-500 font-normal ml-1">µm/a</span>
                <span className="text-neutral-400 ml-2">(offshore marine rule)</span>
              </div>
              <div className="mt-1 text-xs text-neutral-500">
                {result?.resolved_name || (Number.isFinite(lat) && Number.isFinite(lon) ? `${lat.toFixed(4)}, ${lon.toFixed(4)}` : 'Offshore point')}
              </div>
            </div>
          </div>
          <Button
            onClick={onDownloadPdf}
            disabled={downloading}
            variant="outline"
            className="h-11 px-5 self-start md:self-center"
            data-testid="corrosivity-pdf-btn"
          >
            {downloading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Download className="w-4 h-4 mr-2" />}
            Download PDF report
          </Button>
        </div>

        {/* Prominent warning — offshore is a distinct path, not inland */}
        <div
          className="rounded-lg bg-amber-50 border border-amber-300 px-4 py-3 text-sm text-amber-900 flex items-start gap-2"
          data-testid="corrosivity-offshore-warning"
        >
          <AlertTriangle className="w-4 h-4 mt-0.5 flex-shrink-0 text-amber-700" />
          <div className="space-y-1">
            <div className="font-semibold">Offshore marine rule applied — not inland ISO 9223 dose-response</div>
            <p className="leading-relaxed">
              {result?.warning ||
                'Offshore points are classified using the marine/offshore rule, not the inland distance-to-coast chloride logic. Extreme splash/spray regimes may fall beyond normal ISO 9223 dose-response scope and may require specialized assessment.'}
            </p>
          </div>
        </div>

        {/* Key inputs — compact grid */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 pt-2 border-t border-cyan-200">
          <div data-testid="corrosivity-offshore-chloride">
            <div className="text-[11px] uppercase tracking-wider text-neutral-500 font-semibold">Chloride</div>
            <div className="mt-0.5 text-sm font-semibold text-neutral-900">
              {result?.chloride_class || 'S3'} <span className="text-neutral-500 font-normal text-xs">(max marine)</span>
            </div>
          </div>
          <div data-testid="corrosivity-offshore-location">
            <div className="text-[11px] uppercase tracking-wider text-neutral-500 font-semibold">Location class</div>
            <div className="mt-0.5 text-sm font-semibold text-neutral-900 capitalize">
              {result?.location_class || 'marine'}
            </div>
          </div>
          <div>
            <div className="text-[11px] uppercase tracking-wider text-neutral-500 font-semibold">Temperature</div>
            <div className="mt-0.5 text-sm font-semibold text-neutral-900 tabular-nums">
              {Number.isFinite(t) ? t.toFixed(1) : '—'} <span className="text-neutral-500 font-normal text-xs">°C</span>
            </div>
          </div>
          <div>
            <div className="text-[11px] uppercase tracking-wider text-neutral-500 font-semibold">Rel. humidity</div>
            <div className="mt-0.5 text-sm font-semibold text-neutral-900 tabular-nums">
              {Number.isFinite(rh) ? rh.toFixed(1) : '—'} <span className="text-neutral-500 font-normal text-xs">%</span>
            </div>
          </div>
        </div>

        {/* Classification basis notes (from backend metadata) */}
        {notes.length > 0 && (
          <div className="pt-2 border-t border-cyan-200">
            <div className="text-[11px] uppercase tracking-wider text-neutral-500 font-semibold mb-2">
              Classification basis
            </div>
            <ul className="text-xs text-neutral-700 space-y-1 list-disc list-inside">
              {notes.map((n, i) => (<li key={i}>{n}</li>))}
            </ul>
          </div>
        )}

        {/* ── v15.1: Offshore-specific CTA ──────────────────────────── */}
        {/* Offshore queries are high-value leads — FPSO / offshore wind /
            subsea / oil & gas / rigs / port-entry jackets. A dedicated CTA
            converts the right visitor at the right moment. */}
        <div
          className="mt-2 pt-4 border-t border-cyan-200"
          data-testid="corrosivity-offshore-cta"
        >
          <div className="rounded-xl bg-slate-900 text-white p-5 space-y-3">
            <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
              <div className="flex-1 min-w-0">
                <div className="text-[11px] uppercase tracking-[0.2em] text-cyan-300 font-semibold">
                  Offshore asset?
                </div>
                <h3 className="text-base font-semibold mt-1">
                  Get an offshore-spec corrosion study
                </h3>
                <p className="text-xs text-slate-300 mt-2 leading-relaxed">
                  ISO&nbsp;9223 screening is only a starting point for marine
                  infrastructure. Offshore assets face splash/spray zones,
                  cyclic immersion, chloride-laden mist and extreme fatigue
                  cycles — regimes where deterministic ISO classes under-read
                  the real risk. We deliver site-specific studies including
                  cathodic-protection design, coating-system specification,
                  and through-life corrosion-allowance calculations.
                </p>
                <ul className="text-xs text-slate-300 list-disc list-inside mt-3 space-y-1">
                  <li><strong className="text-cyan-200">Offshore wind</strong> — monopile splash zone, transition piece, turbine tower, secondary steel, internal atmosphere</li>
                  <li><strong className="text-cyan-200">Oil &amp; gas / FPSO</strong> — topsides steel, turret mooring, riser hang-off, hull splash zone, flare boom</li>
                  <li><strong className="text-cyan-200">Subsea / pipelines</strong> — CP system life, field-joint coating, pipeline in-service monitoring</li>
                  <li><strong className="text-cyan-200">Ports &amp; jackets</strong> — splash-zone sleeves, tidal-zone monitoring, extended-life retrofits</li>
                </ul>
              </div>
              <Link
                to="/engineering/contact?topic=offshore-corrosion"
                data-testid="corrosivity-offshore-cta-request"
                className="flex-shrink-0 self-start sm:self-center"
              >
                <Button className="h-11 px-5 bg-cyan-500 hover:bg-cyan-400 text-slate-900 font-semibold whitespace-nowrap">
                  Request offshore study <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

// ── v11 + v14: "Cached areas" informational map ─────────────────────
// Compact Leaflet map with TWO marker layers:
//   1. `cities` — curated preloaded cities (named, click-to-autofill).
//   2. `extraCells` — anonymous grid cells that have been cached on demand
//      (e.g. a user searched 'Heathrow Airport' and the cell was downloaded).
//      Rendered as small emerald dots without popups so they don't compete
//      visually with the named cities.
const PreloadedCitiesMap = ({ cities, extraCells = [], onPick }) => {
  const containerRef = useRef(null);
  const mapRef       = useRef(null);
  const layersRef    = useRef({ named: null, extra: null });

  useEffect(() => {
    if (!containerRef.current) return;
    if (mapRef.current) {
      mapRef.current.remove();
      mapRef.current = null;
    }

    const map = L.map(containerRef.current, {
      scrollWheelZoom: false,
      worldCopyJump: true,
      zoomControl: true,
    }).setView([15, 10], 1);

    L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
      attribution: '&copy; OpenStreetMap',
      maxZoom: 6,
      minZoom: 1,
    }).addTo(map);

    const readyIcon = L.divIcon({
      className: 'cams-ready-marker',
      html: '<div style="width:14px;height:14px;border-radius:50%;background:#10b981;border:2px solid #fff;box-shadow:0 0 0 2px rgba(16,185,129,0.35);"></div>',
      iconSize: [14, 14],
      iconAnchor: [7, 7],
    });
    const warmingIcon = L.divIcon({
      className: 'cams-warming-marker',
      html: '<div style="width:14px;height:14px;border-radius:50%;background:#f59e0b;border:2px solid #fff;box-shadow:0 0 0 2px rgba(245,158,11,0.35);"></div>',
      iconSize: [14, 14],
      iconAnchor: [7, 7],
    });

    const extraIcon = L.divIcon({
      className: 'cams-extra-marker',
      html: '<div style="width:9px;height:9px;border-radius:50%;background:#10b981;border:1.5px solid #fff;opacity:0.85;"></div>',
      iconSize: [9, 9],
      iconAnchor: [4.5, 4.5],
    });

    (cities || []).forEach((c) => {
      const icon = c.status === 'ready' ? readyIcon : warmingIcon;
      const m = L.marker([c.lat, c.lon], { icon }).addTo(map);
      const statusLabel = c.status === 'ready'
        ? '<span style="color:#047857;font-weight:600;">Ready · CAMS preloaded</span>'
        : '<span style="color:#b45309;font-weight:600;">Warming cache…</span>';
      // Hover label = city name; click reveals the full popup with CTA.
      m.bindTooltip(c.name, { direction: 'top', offset: [0, -8], sticky: true });
      m.bindPopup(`
        <div style="font-size:12px;min-width:160px;">
          <div style="font-weight:600;margin-bottom:2px;">${c.name}</div>
          <div style="color:#64748b;margin-bottom:4px;">${c.country}</div>
          <div style="margin-bottom:6px;">${statusLabel}</div>
          <button class="cams-pin-pick" data-q="${c.query.replace(/"/g, '&quot;')}" style="background:#10b981;color:white;border:0;border-radius:4px;padding:4px 8px;font-size:11px;cursor:pointer;">
            Use this city →
          </button>
        </div>
      `);
    });

    // v14: anonymous extra cells (cached on demand by user lookups).
    // v18: also includes in-flight "warming" cells (status='warming') so users
    // see a yellow marker while a CAMS cell is downloading.
    // v19: cells now carry an optional `name` (e.g. "Mumbai, Maharashtra,
    // India") so the marker label shows the city instead of raw GPS.
    (extraCells || []).forEach((c) => {
      const isWarming = c.status === 'warming';
      const m = L.marker([c.lat, c.lon], { icon: isWarming ? warmingIcon : extraIcon }).addTo(map);
      const coordStr = `${Number(c.lat).toFixed(4)}, ${Number(c.lon).toFixed(4)}`;
      // Trim the long Nominatim display name so the hover tooltip stays
      // readable: "Mumbai, Maharashtra, India" instead of the full
      // street/zip/country string.
      const shortName = c.name
        ? c.name.split(',').map((s) => s.trim()).filter(Boolean).slice(0, 3).join(', ')
        : null;
      const cellTitle = shortName || (isWarming ? 'Cell warming up' : 'User-cached cell');
      const subtitle = shortName ? coordStr : '';
      const statusLabel = isWarming
        ? '<span style="color:#b45309;font-weight:600;">Warming · CAMS downloading…</span>'
        : '<span style="color:#047857;font-weight:600;">Ready · CAMS cached</span>';
      // Re-run query at the user's actual coords, not the grid center.
      const queryStr = shortName || coordStr;
      const buttonLabel = isWarming ? 'Check this location →' : 'Use this location →';
      // Hover tooltip = short city name (or coord fallback). Same UX as preloaded cities.
      m.bindTooltip(cellTitle, { direction: 'top', offset: [0, -6], sticky: true });
      m.bindPopup(`
        <div style="font-size:12px;min-width:160px;">
          <div style="font-weight:600;margin-bottom:2px;">${cellTitle}</div>
          ${subtitle ? `<div style="color:#64748b;margin-bottom:4px;">${subtitle}</div>` : ''}
          <div style="margin-bottom:6px;">${statusLabel}</div>
          <button class="cams-pin-pick" data-q="${queryStr.replace(/"/g, '&quot;')}" style="background:${isWarming ? '#f59e0b' : '#10b981'};color:white;border:0;border-radius:4px;padding:4px 8px;font-size:11px;cursor:pointer;">
            ${buttonLabel}
          </button>
        </div>
      `);
    });

    // Delegate clicks on the "Use this city" button inside popups
    map.on('popupopen', (e) => {
      const btn = e.popup.getElement()?.querySelector('.cams-pin-pick');
      if (btn && !btn.dataset.wired) {
        btn.dataset.wired = '1';
        btn.addEventListener('click', () => {
          const q = btn.getAttribute('data-q');
          map.closePopup();
          onPick?.(q);
        });
      }
    });

    mapRef.current = map;
    return () => {
      if (mapRef.current) { mapRef.current.remove(); mapRef.current = null; }
    };
  }, [cities, extraCells, onPick]);

  return <div ref={containerRef} className="h-56 w-full" data-testid="corrosivity-preloaded-map" />;
};

export const CorrosivityCalculator = () => {
  const [query, setQuery] = useState('');
  const [loading, setLoading] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [error, setError] = useState('');
  const [technicalError, setTechnicalError] = useState(''); // raw error code/text for debug panel

  // Filter out noisy browser-extension errors that aren't real failures
  // (e.g. Microsoft Edge Copilot trying to postMessage a Fetch Request,
  // which is not structured-cloneable). They're harmless to the UI.
  const setErrorSafe = useCallback((err) => {
    const msg = (err && err.message) || String(err || '') || 'Unknown error';
    if (msg.includes('postMessage') || msg.includes('could not be cloned')) {
      console.warn('Suppressed browser-extension error:', msg);
      return;
    }
    setError(msg);
  }, []);
  const [result, setResult] = useState(null);
  const [rateLimit, setRateLimit] = useState(10);
  const [showDetails, setShowDetails] = useState(false);
  const [showTechnical, setShowTechnical] = useState(false);
  const [industrialOverride, setIndustrialOverride] = useState(false);

  // v20: global guard for browser-extension postMessage errors. Microsoft
  // Edge's Copilot sidebar (and a few other extensions) intercept fetch and
  // try to clone the Request via window.postMessage. The Fetch Request
  // object is NOT structured-cloneable, so the call throws and bubbles up
  // as an unhandled error — visible to the user as a red banner. We
  // swallow it on the window so it never reaches our state.
  useEffect(() => {
    const onError = (e) => {
      const msg = e?.message || e?.error?.message || '';
      if (msg.includes('postMessage') || msg.includes('could not be cloned')) {
        e.preventDefault?.();
        e.stopImmediatePropagation?.();
        console.warn('Suppressed browser-extension postMessage error');
        return false;
      }
    };
    const onRejection = (e) => {
      const msg = e?.reason?.message || String(e?.reason || '');
      if (msg.includes('postMessage') || msg.includes('could not be cloned')) {
        e.preventDefault?.();
        console.warn('Suppressed browser-extension postMessage rejection');
      }
    };
    window.addEventListener('error', onError);
    window.addEventListener('unhandledrejection', onRejection);
    return () => {
      window.removeEventListener('error', onError);
      window.removeEventListener('unhandledrejection', onRejection);
    };
  }, []);

  // v10: indeterminate 4-step loading stepper + reassurance timer
  const [loadingStep, setLoadingStep] = useState(0);
  const [showReassurance, setShowReassurance] = useState(false);
  const stepIntervalRef  = useRef(null);
  const reassuranceRef   = useRef(null);

  // v11: preloaded CAMS cities + pending ETA countdown
  const [preloadedCities, setPreloadedCities] = useState([]);
  const [extraCells, setExtraCells] = useState([]);
  const [etaRemaining, setEtaRemaining] = useState(null);
  const etaIntervalRef = useRef(null);
  const [lastPayload, setLastPayload] = useState(null); // used by pending "Check again" CTA

  // Autocomplete state
  const [suggestions, setSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [activeIdx, setActiveIdx] = useState(-1);

  const inputRef = useRef(null);
  const suppressNextFetch = useRef(false);

  useEffect(() => {
    inputRef.current?.focus();
    fetch(`${API_URL}/api/corrosivity/config`)
      .then((r) => (r.ok ? r.json() : null))
      .then((c) => { if (c && typeof c.rate_limit_day === 'number') setRateLimit(c.rate_limit_day); })
      .catch(() => {});
    // v11: preloaded CAMS cities + v14: also fetch user-cached extra cells
    fetch(`${API_URL}/api/corrosivity/preloaded-cities`)
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => {
        if (d?.cities) setPreloadedCities(d.cities);
        if (Array.isArray(d?.extra_cells)) setExtraCells(d.extra_cells);
      })
      .catch(() => {});
  }, []);

  // v14: refresh the cached-areas list. Called after any successful
  // CAMS-ready lookup so a newly cached grid cell becomes a marker.
  const refreshCachedAreas = () => {
    fetch(`${API_URL}/api/corrosivity/preloaded-cities`)
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => {
        if (d?.cities) setPreloadedCities(d.cities);
        if (Array.isArray(d?.extra_cells)) setExtraCells(d.extra_cells);
      })
      .catch(() => {});
  };

  // Debounced autocomplete — skip if input looks like coordinates
  useEffect(() => {
    if (suppressNextFetch.current) {
      suppressNextFetch.current = false;
      return;
    }
    const q = query.trim();
    if (q.length < 2 || COORD_RE.test(q)) {
      setSuggestions([]);
      setShowSuggestions(false);
      return;
    }
    const handle = setTimeout(async () => {
      try {
        const r = await fetch(`${API_URL}/api/corrosivity/autocomplete?q=${encodeURIComponent(q)}`);
        if (!r.ok) return;
        const data = await r.json();
        setSuggestions(data.results || []);
        setShowSuggestions((data.results || []).length > 0);
        setActiveIdx(-1);
      } catch {/* ignore */}
    }, 250);
    return () => clearTimeout(handle);
  }, [query]);

  const submit = async (payload, modeTag = 'unknown') => {
    setLoading(true);
    setError('');
    setTechnicalError('');
    setResult(null);
    setShowSuggestions(false);
    setLastPayload(payload);  // v11: remember so "Check again" can retry same query
    // Clear any prior ETA countdown
    if (etaIntervalRef.current) { clearInterval(etaIntervalRef.current); etaIntervalRef.current = null; }
    setEtaRemaining(null);
    // Reset + start the indeterminate 4-step stepper. The active index cycles
    // through 0..n-2 and then stays on the last step until the response lands.
    setLoadingStep(0);
    setShowReassurance(false);
    if (stepIntervalRef.current) clearInterval(stepIntervalRef.current);
    if (reassuranceRef.current)  clearTimeout(reassuranceRef.current);
    stepIntervalRef.current = setInterval(() => {
      setLoadingStep((i) => (i < LOADING_STEPS.length - 1 ? i + 1 : i));
    }, STEP_INTERVAL_MS);
    reassuranceRef.current = setTimeout(() => {
      setShowReassurance(true);
    }, REASSURANCE_MS);
    // eslint-disable-next-line no-console
    console.log('[corrosivity] submit →', modeTag, payload);
    try {
      const r = await fetch(`${API_URL}/api/corrosivity/classify-location`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      if (!r.ok) {
        let serverDetail = '';
        try {
          const errBody = await r.json();
          serverDetail = errBody?.detail || '';
        } catch { /* ignore JSON parse */ }
        const friendly =
          r.status === 404
            ? "We couldn't find this location. Please try a more specific address, or enter coordinates directly."
            : r.status === 429
              ? (serverDetail ||
                  "You've reached the daily lookup limit. Need more? Contact us at info@durabledesigns.co.uk and we'll lift the cap.")
              : "We couldn't run this lookup. Please try a more specific address, or enter coordinates directly.";
        // Stash the raw HTTP code + server detail for the technical debug panel only.
        setTechnicalError(`HTTP ${r.status}${serverDetail ? ` · ${serverDetail}` : ''}`);
        throw new Error(friendly);
      }
      const data = await r.json();
      // eslint-disable-next-line no-console
      console.log('[corrosivity] response ←', {
        class: data.corrosivity_class,
        original_query_type: data.location_data?.metadata?.original_query_type,
        resolved_precision: data.location_data?.metadata?.resolved_precision,
        result_mode: data.result_mode,
        resolved_name: data.resolved_name,
        lat: data.latitude,
        lon: data.longitude,
      });
      setResult(data);
      // v11: if backend says pending, start a simple ETA countdown so the
      // user sees remaining time shrink. This is cosmetic — the real clock
      // starts from the response; we don't poll the backend.
      if (data?.status === 'pending_cams_download') {
        const initialEta = Number(data.cams_eta_seconds) || 180;
        setEtaRemaining(initialEta);
        etaIntervalRef.current = setInterval(() => {
          setEtaRemaining((s) => (s == null ? s : Math.max(0, s - 1)));
        }, 1000);
        // v23: refresh the cached-areas map so the freshly-triggered
        // warming cell appears as a yellow marker right away. The backend
        // writes the warming row to Mongo before returning the pending
        // response, so a single short delay covers any replication lag.
        setTimeout(refreshCachedAreas, 500);
      }
      // v14: if the response is CAMS-ready, the resolver may have just
      // cached a brand-new grid cell — refresh the cached-areas map so
      // that cell becomes visible as an extra marker.
      const isCamsReady = data?.cams_source === 'EAC4_reanalysis'
        || data?.current?.data_source === 'CAMS_EAC4';
      if (isCamsReady) {
        refreshCachedAreas();
      }
    } catch (err) {
      setErrorSafe(err);
    } finally {
      if (stepIntervalRef.current) { clearInterval(stepIntervalRef.current); stepIntervalRef.current = null; }
      if (reassuranceRef.current)  { clearTimeout(reassuranceRef.current);   reassuranceRef.current  = null; }
      setShowReassurance(false);
      setLoading(false);
    }
  };

  const onSubmitForm = (e) => {
    e?.preventDefault();
    const raw = query.trim();
    if (!raw) return;

    const common = industrialOverride
      ? { industrial_override: true, original_query: raw }
      : { original_query: raw };

    // 1) Coordinate-string fast path: "51.5074, -0.1278"
    //    Only this path sends explicit lat/lon → backend treats as
    //    `explicit_coordinates / exact_point`.
    const m = raw.match(COORD_RE);
    if (m) {
      const lat = parseFloat(m[1]);
      const lon = parseFloat(m[2]);
      if (Number.isFinite(lat) && Number.isFinite(lon) &&
          lat >= -90 && lat <= 90 && lon >= -180 && lon <= 180) {
        submit({ lat, lon, ...common }, 'coord-string');
        return;
      }
    }

    // 2) Plain string (city OR address). The backend's heuristic decides
    //    `place_query / city_centroid` vs `address_query / address_point`
    //    based on address indicators (street, road, avenida, rua, ...).
    submit({ location: raw, ...common }, 'string');
  };

  const pickSuggestion = (s) => {
    suppressNextFetch.current = true;
    setQuery(s.label);
    setSuggestions([]);
    setShowSuggestions(false);
    // v26.9d: send the picked suggestion's lat/lon directly to the
    // backend. Previously we sent the canonical place `name` (e.g.
    // "Niagara Park") as a string, but bare place names are globally
    // ambiguous — Nominatim would silently round-trip
    // "Niagara Park, 360, Samac" (Bosnia) to "Niagara Park, NSW,
    // Australia" because the latter has higher search prominence.
    // Open-Meteo's autocomplete already gave us the exact lat/lon of
    // the picked place, so re-geocoding throws that information away.
    //
    // We still pass the full label as `original_query` so the result
    // surfaces the user's chosen place name verbatim, and so the
    // industrial-keyword detection on the backend continues to work
    // (it scans the original_query for "refinery", "dock", etc.).
    const lat = Number(s.lat);
    const lon = Number(s.lon);
    const payload = Number.isFinite(lat) && Number.isFinite(lon)
      ? { lat, lon, original_query: s.label }
      : { location: (s.name && s.name.trim()) || s.label, original_query: s.label };
    if (industrialOverride) payload.industrial_override = true;
    submit(payload, 'autocomplete-pick-coords');
  };

  // No-op kept for compatibility with the input's onChange handler.
  const onQueryChange = (val) => {
    setQuery(val);
  };

  const onInputKeyDown = (e) => {
    if (!showSuggestions || suggestions.length === 0) return;
    if (e.key === 'ArrowDown') { e.preventDefault(); setActiveIdx((i) => Math.min(i + 1, suggestions.length - 1)); }
    else if (e.key === 'ArrowUp')   { e.preventDefault(); setActiveIdx((i) => Math.max(i - 1, -1)); }
    else if (e.key === 'Enter' && activeIdx >= 0) { e.preventDefault(); pickSuggestion(suggestions[activeIdx]); }
    else if (e.key === 'Escape')    { setShowSuggestions(false); }
  };

  const downloadPdf = async () => {
    if (!result) return;
    setDownloading(true);
    try {
      const r = await fetch(`${API_URL}/api/corrosivity/report`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(result),
      });
      if (!r.ok) throw new Error('PDF generation failed');
      const blob = await r.blob();
      const url  = URL.createObjectURL(blob);
      const a    = document.createElement('a');
      a.href     = url;
      const base = (result.resolved_name || 'report').split(',')[0].replace(/\s+/g, '_');
      a.download = `corrosivity-${base}.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (err) {
      setErrorSafe(err);
    } finally {
      setDownloading(false);
    }
  };

  // Derive display values from the new response shape
  // Per the v12 contract, per-metal classifications now live under
  // `current.metals.*` and `historical.metals.*` with shape
  //   { class, r_corr, r_corr_unit }
  // The legacy top-level `result.metals` block (used pre-v12 for the
  // 4-metals table) is still emitted by the route — we keep it as a
  // fallback so old cached responses still render.
  const legacyMetals      = result?.metals || {};
  const currentMetalsRaw  = result?.current?.metals || {};
  const historicalMetalsRaw = result?.historical?.metals || {};

  // Normalize a single metal entry from either the new shape ({class, r_corr})
  // or the legacy shape ({primary_class, r_corr, r_corr_unit, method, validity_warnings}).
  const normalizeMetal = (m) => {
    if (!m) return null;
    return {
      class: m.class || m.primary_class,
      r_corr: m.r_corr,
      r_corr_unit: m.r_corr_unit,
      method: m.method,
      validity_warnings: m.validity_warnings || [],
    };
  };
  const currentMetals = ['steel', 'zinc', 'copper', 'aluminium'].reduce((acc, k) => {
    acc[k] = normalizeMetal(currentMetalsRaw[k]) || normalizeMetal(legacyMetals[k]);
    return acc;
  }, {});
  const historicalMetals = ['steel', 'zinc', 'copper', 'aluminium'].reduce((acc, k) => {
    acc[k] = normalizeMetal(historicalMetalsRaw[k]);
    return acc;
  }, {});
  const hasHistoricalMetals = Object.values(historicalMetals).some((m) => m && m.class);

  const steel             = currentMetals.steel;  // headline still reads `steel?.r_corr` etc.
  const lat               = result?.latitude;
  const lon               = result?.longitude;
  const hasCoord          = Number.isFinite(lat) && Number.isFinite(lon);
  const primary           = result?.corrosivity_class || steel?.class || 'C1';
  const resolvedName      = result?.resolved_name || '';
  const calibrationNotes  = result?.location_data?.metadata?.metadata_calibration_notes
                          || result?.location_data?.metadata?.calibration_notes || [];
  const distanceToSeaKm   = result?.distance_to_sea_km;
  const metaWarnings      = result?.location_data?.metadata?.warnings || {};
  const industrialWarning = metaWarnings.industrial_site;
  const so2PlaceholderWarn= metaWarnings.so2_placeholder;
  const representativeWarn= metaWarnings.representative_result;
  const siteSpecificNote  = metaWarnings.site_specific_note;
  const isIndustrialSite  = result?.settlement_context === 'industrial_site'
                          || result?.location_data?.metadata?.industrial_override_applied === true;

  // ── New metadata-driven precision/labelling (overrides legacy heuristics) ──
  const md                = result?.location_data?.metadata || {};
  const resolvedPrecision = md.resolved_precision;        // exact_point | address_point | city_centroid | representative_place
  const originalQueryType = md.original_query_type;       // explicit_coordinates | address_query | place_query
  const coordinateSource  = md.coordinate_source;
  const resultMode        = result?.result_mode;          // site_specific | representative — authoritative
  const isRepresentative  = resultMode === 'representative';
  const isSiteSpecific    = resultMode === 'site_specific';
  const isCityCentroid    = resolvedPrecision === 'city_centroid';
  const isAddressPoint    = resolvedPrecision === 'address_point';
  // Prefer authoritative metadata; fall back to the legacy enum so older cached
  // responses still render a sensible badge.
  const precisionMeta     = (resolvedPrecision && PRECISION_META_BY_RESOLVED[resolvedPrecision])
                          || (result?.precision_level ? PRECISION_META[result.precision_level] : null);

  // ── v10 — CAMS snapshots + trend + fallback detection ─────────────────
  const currentSnapshot    = result?.current || null;
  const historicalSnapshot = result?.historical || null;
  const hasHistorical      = !!(historicalSnapshot && historicalSnapshot.corrosivity_class);

  // ── v13 — derive trend from steel class + r_corr (FE source of truth) ─
  // We deliberately ignore `result.trend` from the backend because it was
  // computed from ΔPd (SO₂ deposition only) and could disagree with the
  // class change — e.g. Singapore went C3→C4 but ΔPd was negative, so the
  // backend said "improving" while the class clearly worsened. Per the
  // contract, the badge follows steel class first; r_corr ratio is only the
  // tiebreaker when both classes match.
  const trend = deriveTrend(currentMetals.steel, historicalMetals.steel);

  // ── v11 — three CAMS states ──────────────────────────────────────────
  //   ready    : current.data_source === 'CAMS_EAC4' → normal results, no fallback banner
  //   pending  : status === 'pending_cams_download'  → neutral waiting card + retry CTA
  //   offshore : status === 'offshore'               → dedicated marine card (v15)
  //   fallback : anything else (placeholder / distance_proxy / estimated)
  const isPending  = result?.status === 'pending_cams_download';
  const isOffshore = result?.status === 'offshore';
  const camsReady = !isPending && !isOffshore && (
    result?.cams_source === 'EAC4_reanalysis'
    || currentSnapshot?.data_source === 'CAMS_EAC4'
  );
  const usesFallbackAtm = !isPending && !isOffshore && !camsReady && hasFallbackAtmData(result);

  // What mode will the current search-bar contents submit in? Two modes only:
  //  - typed numeric coords ("explicit_coordinates / exact_point" lane)
  //  - any other string ("place_query" or "address_query" — backend decides
  //    based on its address-indicator heuristic).
  // The badge after the result lands is always driven by the authoritative
  // `metadata.resolved_precision` from the backend.
  const trimmedQuery = query.trim();
  const coordStringMatch = trimmedQuery.match(COORD_RE);
  const pendingMode = !trimmedQuery
    ? null
    : coordStringMatch
      ? {
          kind: 'coord',
          label: 'Coordinates (typed)',
          detail: `${parseFloat(coordStringMatch[1]).toFixed(4)}, ${parseFloat(coordStringMatch[2]).toFixed(4)} — exact_point`,
        }
      : {
          kind: 'string',
          label: 'Place / address',
          detail: 'Backend will resolve as place or address (city centroid vs address-level)',
        };

  return (
    <div className="min-h-screen bg-white">
      <SEO
        title="Atmospheric Corrosivity Calculator"
        description="Free atmospheric corrosivity classifier for steel, zinc, copper and aluminium — ISO 9223:2012. Search any city, address, or coordinates."
        path="/engineering/calculator/corrosivity"
      />

      {/* ── Header ──────────────────────────────────────────────────── */}
      <header className="border-b border-neutral-200">
        <div className="max-w-6xl mx-auto px-6 py-5 flex items-center justify-between">
          <Link to="/engineering/apps" className="text-sm text-neutral-500 hover:text-neutral-900 inline-flex items-center gap-1.5" data-testid="back-to-apps">
            <ArrowRight className="w-3.5 h-3.5 rotate-180" /> Back to apps
          </Link>
          <div className="text-[11px] uppercase tracking-[0.2em] text-emerald-700 font-semibold">
            Durable Designs · Engineering tools
          </div>
        </div>
      </header>

      {/* ── Hero ─────────────────────────────────────────────────────── */}
      <section className="max-w-6xl mx-auto px-6 pt-12 pb-6">
        <div className="text-[11px] uppercase tracking-[0.2em] text-emerald-700 font-semibold mb-3">
          ISO 9223:2012
        </div>
        <h1 className="text-4xl sm:text-5xl font-extrabold text-neutral-900 tracking-tight leading-[1.1] mb-3" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
          Atmospheric corrosivity, anywhere.
        </h1>
        <p className="text-base text-neutral-600 max-w-2xl leading-relaxed">
          Enter a city, street address or latitude/longitude. We resolve climate,
          SO₂ and chloride exposure, then return the ISO&nbsp;9223:2012 corrosivity
          class (C1–CX) and corrosion rate for steel, zinc, copper and aluminium.
        </p>
      </section>

      {/* ── Search box (left) + info sidebar (right) ─────────────────── */}
      <section className="max-w-6xl mx-auto px-6 pb-8">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Left: form */}
          <div className="lg:col-span-2">
            <form onSubmit={onSubmitForm} className="flex gap-2 relative">
          <div className="relative flex-1">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-neutral-400 pointer-events-none" />
            <Input
              ref={inputRef}
              type="text"
              value={query}
              onChange={(e) => onQueryChange(e.target.value)}
              onKeyDown={onInputKeyDown}
              onFocus={() => suggestions.length > 0 && setShowSuggestions(true)}
              onBlur={() => setTimeout(() => setShowSuggestions(false), 150)}
              placeholder="City, address (e.g. 'Avenida da Liberdade, Lisbon'), or 51.5074, -0.1278"
              className="pl-10 h-12 text-base"
              disabled={loading}
              autoComplete="off"
              data-testid="corrosivity-town-input"
            />
            {showSuggestions && suggestions.length > 0 && (
              <div
                className="absolute top-full left-0 right-0 mt-1 z-50 bg-white border border-neutral-200 rounded-lg shadow-lg overflow-hidden max-h-72 overflow-y-auto"
                data-testid="corrosivity-autocomplete-list"
              >
                {suggestions.map((s, i) => (
                  <button
                    key={`${s.lat}-${s.lon}-${i}`}
                    type="button"
                    onMouseDown={(e) => e.preventDefault()}
                    onClick={() => pickSuggestion(s)}
                    className={`w-full text-left px-4 py-2.5 text-sm flex items-center gap-2 border-b border-neutral-100 last:border-0 ${
                      i === activeIdx ? 'bg-emerald-50' : 'hover:bg-neutral-50'
                    }`}
                    data-testid={`corrosivity-suggestion-${i}`}
                  >
                    <MapPin className="w-3.5 h-3.5 text-neutral-400 flex-shrink-0" />
                    <span className="truncate">{s.label}</span>
                  </button>
                ))}
              </div>
            )}
          </div>
          <Button
            type="submit"
            disabled={loading || !query.trim()}
            className="h-12 px-6 bg-emerald-600 hover:bg-emerald-700 text-white font-semibold"
            data-testid="corrosivity-check-btn"
          >
            {loading ? (<><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Looking up…</>)
                     : (<>Check Corrosivity <ArrowRight className="w-4 h-4 ml-2" /></>)}
          </Button>
        </form>

        {loading && (
          <LoadingStepper activeIdx={loadingStep} showReassurance={showReassurance} />
        )}

        {error && (
          <div className="mt-4 p-4 rounded-lg bg-red-50 border border-red-200 text-red-800 text-sm flex items-start gap-2" data-testid="corrosivity-error">
            <AlertTriangle className="w-4 h-4 mt-0.5 flex-shrink-0" />
            <div>
              <span>{error}</span>
              {technicalError && (
                <div className="text-[11px] text-red-500/70 mt-1 font-mono" data-testid="corrosivity-error-technical">
                  {technicalError}
                </div>
              )}
            </div>
          </div>
        )}

        {/* Query-type indicator — shows exactly what the next submit will send */}
        {pendingMode && (
          <div
            className={`mt-3 flex items-center gap-2 text-xs ${
              pendingMode.kind === 'string' ? 'text-amber-800' : 'text-emerald-800'
            }`}
            data-testid="corrosivity-pending-mode"
          >
            <span
              className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full font-semibold uppercase tracking-wider ${
                pendingMode.kind === 'string'
                  ? 'bg-amber-50 border border-amber-200'
                  : 'bg-emerald-50 border border-emerald-200'
              }`}
            >
              {pendingMode.kind === 'string' ? <MapPin className="w-3 h-3" /> : <Crosshair className="w-3 h-3" />}
              {pendingMode.label}
            </span>
            <span className="text-neutral-500 truncate">{pendingMode.detail}</span>
          </div>
        )}

        {/* Industrial facility override */}
        <label
          className="mt-4 flex items-start gap-2.5 cursor-pointer group select-none"
          data-testid="corrosivity-industrial-toggle-label"
        >
          <input
            type="checkbox"
            checked={industrialOverride}
            onChange={(e) => setIndustrialOverride(e.target.checked)}
            disabled={loading}
            className="mt-0.5 w-4 h-4 rounded border-neutral-300 text-emerald-600 focus:ring-emerald-500 cursor-pointer"
            data-testid="corrosivity-industrial-toggle"
          />
          <span className="text-sm text-neutral-700 leading-snug">
            <span className="font-medium">Treat this as an industrial facility / service atmosphere</span>
            <span className="block text-xs text-neutral-500 mt-0.5">
              This option adds an industrial-service caution to the report. It
              does not change the underlying ISO&nbsp;9223 corrosivity class.
            </span>
          </span>
        </label>

        {/* ── v22: Pending CAMS card rendered HIGH on the page ────────────
            Previously placed below the entire result section, but most users
            don't scroll that far on a fresh search and missed the queued
            status entirely. Showing it inline right after the toggle keeps
            the progress bar + auto-poll visible above the map. */}
        {result && isPending && (
          <div className="mt-5" data-testid="corrosivity-results-pending">
            <PendingCamsCard
              result={result}
              onRetry={() => lastPayload && submit(lastPayload, 'retry-pending')}
              retrying={loading}
              etaRemaining={etaRemaining}
              onScrollToMap={() => {
                const el = document.querySelector('[data-testid="corrosivity-preloaded-map"]');
                if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
              }}
            />
          </div>
        )}

        {/* ── v11 + v14: Cached CAMS areas — inline below search/toggle ────
            Shows curated preloaded cities AND any grid cells cached on demand
            by user lookups. After any successful CAMS-ready lookup the FE
            re-fetches /preloaded-cities so newly cached cells appear here. */}
        {(preloadedCities.length > 0 || extraCells.length > 0) && (
          <div
            className="mt-5 rounded-xl border border-emerald-100 bg-emerald-50/30 overflow-hidden"
            data-testid="corrosivity-preloaded-section"
          >
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-1 px-4 pt-3 pb-2">
              <div>
                <div className="text-[11px] uppercase tracking-[0.2em] text-emerald-700 font-semibold">
                  Cached CAMS areas
                </div>
                <p className="text-xs text-neutral-600 mt-0.5">
                  Click any marker for an instant lookup. New areas you query
                  are added here automatically once cached.
                </p>
              </div>
              <div className="flex items-center gap-3 text-[11px] text-neutral-500">
                <span className="inline-flex items-center gap-1.5" data-testid="corrosivity-preloaded-legend-ready">
                  <span className="w-2.5 h-2.5 rounded-full bg-emerald-500" /> Preloaded
                </span>
                <span className="inline-flex items-center gap-1.5" data-testid="corrosivity-preloaded-legend-extra">
                  <span className="w-2 h-2 rounded-full bg-emerald-500 opacity-80" /> User-cached
                </span>
                <span className="inline-flex items-center gap-1.5" data-testid="corrosivity-preloaded-legend-warming">
                  <span className="w-2.5 h-2.5 rounded-full bg-amber-500" /> Warming
                </span>
              </div>
            </div>
            <PreloadedCitiesMap
              cities={preloadedCities}
              extraCells={extraCells}
              onPick={(q) => {
                setQuery(q);
                suppressNextFetch.current = true;
                submit({ location: q, original_query: q }, 'map-pick');
              }}
            />
          </div>
        )}
          </div>

          {/* Right: info sidebar (always visible) */}
          <aside className="space-y-4" data-testid="corrosivity-info-sidebar">
            <Card className="border-gray-200 bg-gray-50">
              <CardContent className="pt-5 pb-5">
                <h3 className="text-sm font-bold text-gray-800 mb-3">What is ISO 9223?</h3>
                <p className="text-xs text-gray-600 leading-relaxed">
                  ISO&nbsp;9223:2012 classifies the corrosivity of outdoor atmospheres
                  into six categories — C1 (very low) to CX (extreme) — based on
                  the three key environmental factors that drive metal corrosion:
                  the temperature-humidity complex, sulfur dioxide (SO&#8322;)
                  pollution, and airborne chloride salinity.
                </p>
              </CardContent>
            </Card>

            <Card className="border-emerald-100 bg-emerald-50/40" data-testid="corrosivity-cams-info">
              <CardContent className="pt-5 pb-5">
                <h3 className="text-sm font-bold text-gray-800 mb-3">
                  How CAMS enhances the assessment
                </h3>
                <p className="text-xs text-gray-600 leading-relaxed">
                  This tool combines ISO&nbsp;9223 classification with the
                  Copernicus Atmosphere Monitoring Service (CAMS) — a global
                  atmospheric reanalysis providing gridded SO&#8322;, humidity
                  and temperature. CAMS supplies the regional background, while
                  a near-coastal chloride model derived from distance-to-sea
                  provides the marine contribution. Combining both inputs yields
                  two matched snapshots — the current period and a 2013
                  reference — so a single site can be compared over time.
                </p>
              </CardContent>
            </Card>

            <Card className="border-gray-200 bg-gray-50">
              <CardContent className="pt-5 pb-5">
                <h3 className="text-sm font-bold text-gray-800 mb-3">Key Factors</h3>
                <ul className="space-y-2 text-xs text-gray-600">
                  <li><strong className="text-gray-700">Location</strong> — determines climate (temperature, relative humidity) from historical data.</li>
                  <li><strong className="text-gray-700">Marine exposure</strong> — distance to sea controls airborne chloride (S0–S3), the dominant driver in coastal environments.</li>
                  <li><strong className="text-gray-700">SO&#8322;</strong> — industrial and urban pollution accelerates corrosion; estimated from Copernicus CAMS atmospheric data.</li>
                  <li><strong className="text-gray-700">Humidity</strong> — corrosion is fastest at sustained high relative humidity (&gt;80% RH at &gt;0&nbsp;°C).</li>
                </ul>
              </CardContent>
            </Card>

            <Card className="border-amber-100 bg-amber-50/50">
              <CardContent className="pt-5 pb-5">
                <h3 className="text-sm font-bold text-gray-800 mb-3">Site-specific vs. representative</h3>
                <p className="text-xs text-gray-600 leading-relaxed">
                  For engineering use, enter a full address or exact coordinates.
                  A city-name query gives a representative estimate only — marine
                  and pollution exposure can vary significantly within a city,
                  especially near the coast or industrial areas.
                </p>
              </CardContent>
            </Card>
          </aside>
        </div>
      </section>

      {/* ── Pending CAMS download — rendered inline above the map (v22) ─ */}

      {/* ── Offshore marine classification ──────────────────────────── */}
      {result && isOffshore && (
        <section className="max-w-6xl mx-auto px-6 pb-16" data-testid="corrosivity-results-offshore">
          <OffshoreResultCard
            result={result}
            downloading={downloading}
            onDownloadPdf={downloadPdf}
          />
        </section>
      )}

      {/* ── Results ─────────────────────────────────────────────────── */}
      {result && !isPending && !isOffshore && (
        <section className="max-w-6xl mx-auto px-6 pb-16 space-y-6" data-testid="corrosivity-results">
          {/* Headline result */}
          <Card className="border-2" style={{ borderColor: CLASS_BG[primary] }}>
            <CardContent className="p-6">
              <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                <div>
                  <div className="flex items-center gap-2 text-xs text-neutral-500 mb-2">
                    <MapPin className="w-3.5 h-3.5" />
                    <span className="truncate">{resolvedName}</span>
                    {precisionMeta && (
                      <span
                        className={`ml-2 inline-flex items-center gap-1 text-[10px] uppercase tracking-wider font-semibold px-2 py-0.5 rounded-full ${
                          precisionMeta.tone === 'amber'
                            ? 'bg-amber-50 text-amber-800 border border-amber-200'
                            : 'bg-emerald-50 text-emerald-800 border border-emerald-200'
                        }`}
                        data-testid="corrosivity-precision-badge"
                      >
                        <Crosshair className="w-3 h-3" />
                        {precisionMeta.label}
                      </span>
                    )}
                    {resultMode && (
                      <span
                        className={`inline-flex items-center text-[10px] uppercase tracking-wider font-semibold px-2 py-0.5 rounded-full ${
                          isRepresentative
                            ? 'bg-amber-100 text-amber-900 border border-amber-300'
                            : 'bg-emerald-100 text-emerald-900 border border-emerald-300'
                        }`}
                        data-testid="corrosivity-result-mode-badge"
                      >
                        {isRepresentative ? 'Representative · preview' : 'Site-specific'}
                      </span>
                    )}
                  </div>
                  <div className="flex flex-wrap items-baseline gap-3">
                    <ClassPill cls={primary} size="lg" />
                    <span className="text-lg text-neutral-700 font-medium">
                      {CLASS_LABEL[primary]}
                    </span>
                    {trend && <TrendBadge trend={trend} />}
                  </div>
                  {currentSnapshot?.year && (
                    <div className="mt-1 text-[11px] uppercase tracking-wider text-emerald-700 font-semibold" data-testid="corrosivity-headline-year">
                      Current corrosivity class ({currentSnapshot.year})
                    </div>
                  )}
                  {trend && currentSnapshot?.year && historicalSnapshot?.year && (
                    <div className="mt-1 text-xs text-neutral-500" data-testid="corrosivity-trend-period">
                      Comparison period: {historicalSnapshot.year} → {currentSnapshot.year}
                    </div>
                  )}
                  <div className="mt-3 text-sm text-neutral-600">
                    Carbon steel · <strong>{steel?.r_corr?.toFixed(2)}</strong> {steel?.r_corr_unit}
                    <span className="text-neutral-400 ml-2">
                      (ISO 9223 dose-response{steel?.method ? `, ${steel.method}` : ''})
                    </span>
                  </div>
                </div>
                <Button
                  onClick={downloadPdf}
                  disabled={downloading}
                  variant="outline"
                  className="h-11 px-5 self-start md:self-center"
                  data-testid="corrosivity-pdf-btn"
                >
                  {downloading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Download className="w-4 h-4 mr-2" />}
                  Download PDF report
                </Button>
              </div>
            </CardContent>
          </Card>

          {/* Result-mode banner — driven by metadata.result_mode (authoritative).
              Uses the exact warning text emitted by the backend so the wording
              stays in lockstep with the resolver. */}
          {isRepresentative && (
            <div
              className="rounded-lg bg-amber-50 border border-amber-300 px-4 py-3 text-sm text-amber-900 flex items-start gap-2"
              data-testid="corrosivity-representative-banner"
            >
              <Info className="w-4 h-4 mt-0.5 flex-shrink-0 text-amber-700" />
              <div className="space-y-0.5">
                <div className="font-semibold">Representative city estimate · preview only</div>
                <p>
                  {representativeWarn ||
                    'Representative city estimate - not a site-specific engineering classification. For engineering use, provide exact address or coordinates.'}
                </p>
              </div>
            </div>
          )}
          {isSiteSpecific && (
            <div
              className="rounded-lg bg-emerald-50 border border-emerald-300 px-4 py-3 text-sm text-emerald-900 flex items-start gap-2"
              data-testid="corrosivity-site-specific-banner"
            >
              <Crosshair className="w-4 h-4 mt-0.5 flex-shrink-0 text-emerald-700" />
              <div className="space-y-0.5">
                <div className="font-semibold">Site-specific result</div>
                <p>{siteSpecificNote || 'Site-specific estimate based on the resolved location.'}</p>
              </div>
            </div>
          )}

          {/* Industrial-site caution — shown when backend flags industrial intent */}
          {isIndustrialSite && (industrialWarning || so2PlaceholderWarn) && (
            <div
              className="rounded-lg bg-orange-50 border border-orange-200 px-4 py-3 text-sm text-orange-900 space-y-2"
              data-testid="corrosivity-industrial-warning"
            >
              <div className="flex items-start gap-2 font-semibold">
                <AlertTriangle className="w-4 h-4 mt-0.5 flex-shrink-0 text-orange-700" />
                <span>Industrial-site caution</span>
              </div>
              {industrialWarning && (
                <p className="pl-6 text-orange-800 leading-relaxed">{industrialWarning}</p>
              )}
              {so2PlaceholderWarn && (
                <p className="pl-6 text-orange-800 leading-relaxed">{so2PlaceholderWarn}</p>
              )}
            </div>
          )}

          {/* Fallback atmospheric data notice — shown when CAMS was unavailable
              and the backend used a placeholder / distance-proxy estimate. */}
          {usesFallbackAtm && (
            <div
              className="rounded-lg bg-amber-50 border border-amber-200 px-4 py-3 text-sm text-amber-900 flex items-start gap-2"
              data-testid="corrosivity-fallback-atm-banner"
            >
              <Info className="w-4 h-4 mt-0.5 flex-shrink-0 text-amber-700" />
              <div className="space-y-0.5">
                <div className="font-semibold">Partial fallback estimate used</div>
                <p>
                  Live atmospheric data could not be fully retrieved for this
                  query. A fallback estimate was used for part of the assessment.
                </p>
              </div>
            </div>
          )}

          {/* Historical comparison (current vs 2013) — always rendered when the
              product supports it; degrades gracefully when the 2013 grid-cell
              snapshot is unavailable. */}
          {(currentSnapshot || historicalSnapshot) && (
            <Card data-testid="corrosivity-historical-comparison">
              <CardContent className="p-6 space-y-4">
                <div className="flex flex-wrap items-center justify-between gap-3">
                  <div>
                    <div className="text-[11px] uppercase tracking-[0.2em] text-neutral-500 font-semibold">
                      Historical comparison
                    </div>
                    <div className="text-sm font-semibold text-neutral-900 mt-0.5">
                      {hasHistorical && historicalSnapshot?.year && currentSnapshot?.year
                        ? `${historicalSnapshot.year} vs ${currentSnapshot.year}`
                        : 'Current snapshot'}
                    </div>
                  </div>
                  {hasHistorical && (
                    <div className="flex flex-wrap gap-1.5">
                      <DeltaChip label="ΔPd" value={result?.trend?.delta_Pd_pct} unit="%" higherIsWorse />
                      <DeltaChip label="ΔSd" value={result?.trend?.delta_Sd_pct} unit="%" higherIsWorse />
                      {currentSnapshot?.RH_pct != null && historicalSnapshot?.RH_pct != null && (
                        <DeltaChip
                          label="ΔRH"
                          value={currentSnapshot.RH_pct - historicalSnapshot.RH_pct}
                          unit="pp"
                          higherIsWorse={false}
                        />
                      )}
                      {currentSnapshot?.T_C != null && historicalSnapshot?.T_C != null && (
                        <DeltaChip
                          label="ΔT"
                          value={currentSnapshot.T_C - historicalSnapshot.T_C}
                          unit="°C"
                          higherIsWorse={false}
                        />
                      )}
                    </div>
                  )}
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <SnapshotCard
                    title="Current"
                    snapshot={currentSnapshot}
                    variant="current"
                  />
                  {hasHistorical ? (
                    <SnapshotCard
                      title="Historical"
                      snapshot={historicalSnapshot}
                      variant="historical"
                    />
                  ) : (
                    <HistoricalUnavailableCard />
                  )}
                </div>
                <p className="text-xs text-neutral-500 leading-relaxed">
                  Snapshots are built from Copernicus Atmosphere Monitoring
                  Service (CAMS) reanalysis data — SO₂ deposition (Pd), chloride
                  deposition (Sd), relative humidity and temperature — then
                  classified against ISO&nbsp;9223:2012. Detailed probabilistic
                  outputs are intentionally not surfaced in this screening
                  report. Current year reflects the latest complete CAMS EAC4
                  reanalysis annual dataset, which is published with an
                  approximate 12–18&nbsp;month lag.
                </p>
              </CardContent>
            </Card>
          )}

          {/* Exposure summary — user-friendly */}
          <Card>
            <CardContent className="p-6">
              <div className="text-[11px] uppercase tracking-[0.2em] text-neutral-500 font-semibold mb-3">
                Exposure summary
              </div>
              <div className="divide-y divide-neutral-100">
                <SummaryRow
                  icon={Building2}
                  label="Settlement"
                  value={SETTLEMENT_LABELS[result.settlement_context] || result.settlement_context}
                  testId="summary-settlement"
                />
                <SummaryRow
                  icon={Waves}
                  label="Marine context"
                  value={MARINE_CONTEXT_LABELS[result.marine_context] || result.marine_context}
                  testId="summary-marine"
                />
                <SummaryRow
                  icon={Wind}
                  label="Sulfur environment"
                  value={SO2_ATMOSPHERE_LABELS[result.iso_so2_atmosphere] || result.iso_so2_atmosphere}
                  testId="summary-so2"
                />
                <SummaryRow
                  icon={AlertTriangle}
                  label="Corrosivity"
                  value={`${primary} — ${CLASS_LABEL[primary]}`}
                  testId="summary-corrosivity"
                />
              </div>
            </CardContent>
          </Card>

          {/* Map + Per-metal table */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <Card className="overflow-hidden">
              <CardContent className="p-0">
                {hasCoord && (
                  <MapPanel
                    lat={lat}
                    lon={lon}
                    displayName={resolvedName}
                    primary={primary}
                    classLabel={CLASS_LABEL[primary]}
                    steel={steel}
                    uncertaintyKm={result.distance_uncertainty_km}
                    precisionLevel={result.precision_level}
                    distanceToSeaKm={result.distance_to_sea_km}
                  />
                )}
              </CardContent>
            </Card>

            <Card>
              <CardContent className="p-6">
                <div className="text-[11px] uppercase tracking-[0.2em] text-neutral-500 font-semibold mb-4">
                  Per-metal corrosion rates · ISO 9223 dose-response
                </div>
                <div className={`grid ${hasHistoricalMetals ? 'grid-cols-1 lg:grid-cols-2 gap-6' : 'grid-cols-1'}`}>
                  {/* Current year metals table */}
                  <div data-testid="corrosivity-metals-current">
                    <div className="text-xs font-semibold text-neutral-700 mb-2 flex items-center gap-2">
                      <span className="inline-block w-2 h-2 rounded-full bg-emerald-500" />
                      Current{currentSnapshot?.year ? ` (${currentSnapshot.year})` : ''}
                    </div>
                    <div className="overflow-x-auto">
                      <table className="w-full text-sm">
                        <thead className="text-neutral-500 text-xs uppercase tracking-wider">
                          <tr className="border-b border-neutral-200">
                            <th className="text-left pb-2 font-medium">Metal</th>
                            <th className="text-left pb-2 font-medium">Class</th>
                            <th className="text-right pb-2 font-medium">r<sub>corr</sub></th>
                          </tr>
                        </thead>
                        <tbody className="text-neutral-800">
                          {['steel', 'zinc', 'copper', 'aluminium'].map((m) => {
                            const d = currentMetals[m];
                            if (!d) return null;
                            return (
                              <tr
                                key={m}
                                className="border-b border-neutral-100"
                                data-testid={`metal-row-${m}`}
                                data-metal-current-class={d.class || ''}
                              >
                                <td className="py-3 font-medium capitalize">{m}</td>
                                <td className="py-3"><ClassPill cls={d.class} /></td>
                                <td className="py-3 text-right tabular-nums font-semibold">
                                  {d.r_corr != null ? d.r_corr.toFixed(2) : '—'}
                                  <span className="text-neutral-500 font-normal ml-1">{d.r_corr_unit}</span>
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  </div>

                  {/* Historical year metals table — rendered side-by-side when available */}
                  {hasHistoricalMetals ? (
                    <div data-testid="corrosivity-metals-historical">
                      <div className="text-xs font-semibold text-neutral-700 mb-2 flex items-center gap-2">
                        <span className="inline-block w-2 h-2 rounded-full bg-neutral-400" />
                        Historical{historicalSnapshot?.year ? ` (${historicalSnapshot.year})` : ''}
                      </div>
                      <div className="overflow-x-auto">
                        <table className="w-full text-sm">
                          <thead className="text-neutral-500 text-xs uppercase tracking-wider">
                            <tr className="border-b border-neutral-200">
                              <th className="text-left pb-2 font-medium">Metal</th>
                              <th className="text-left pb-2 font-medium">Class</th>
                              <th className="text-right pb-2 font-medium">r<sub>corr</sub></th>
                            </tr>
                          </thead>
                          <tbody className="text-neutral-800">
                            {['steel', 'zinc', 'copper', 'aluminium'].map((m) => {
                              const d = historicalMetals[m];
                              if (!d) return null;
                              return (
                                <tr
                                  key={m}
                                  className="border-b border-neutral-100"
                                  data-testid={`metal-row-historical-${m}`}
                                  data-metal-historical-class={d.class || ''}
                                >
                                  <td className="py-3 font-medium capitalize">{m}</td>
                                  <td className="py-3"><ClassPill cls={d.class} /></td>
                                  <td className="py-3 text-right tabular-nums font-semibold">
                                    {d.r_corr != null ? d.r_corr.toFixed(2) : '—'}
                                    <span className="text-neutral-500 font-normal ml-1">{d.r_corr_unit}</span>
                                  </td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  ) : null}
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Sd-vs-distance deposition profile (v26.6) — shows the model's
              reasoning visually. Sits above the collapsible details so it's
              the first thing users see after the headline class.
              v26.9: gated on precision_level. City-name queries
              (`city_centroid`) deliberately don't render the chart because
              the underlying distance-to-sea is a *representative* value
              (typically the 2 km floor, the literal centroid distance, or
              clamped to a water-body cap) — plotting a precise-looking
              decay curve against a representative distance gives a false
              impression of site-specific resolution. The chart only renders
              for exact coords or full street-address queries. */}
          {result.sd_profile
            && (result.precision_level === 'high' || result.precision_level === 'medium') && (
            <SdProfileChart
              profile={result.sd_profile}
              waterBody={result.water_body}
            />
          )}

          {/* Details & method (collapsible) */}
          <Card>
            <CardContent className="p-0">
              <button
                type="button"
                onClick={() => setShowDetails((v) => !v)}
                className="w-full flex items-center justify-between px-6 py-4 text-left hover:bg-neutral-50 transition-colors"
                data-testid="corrosivity-details-toggle"
              >
                <span className="text-[11px] uppercase tracking-[0.2em] text-neutral-500 font-semibold">
                  Details & method
                </span>
                <ChevronDown className={`w-4 h-4 text-neutral-400 transition-transform ${showDetails ? 'rotate-180' : ''}`} />
              </button>
              {showDetails && (
                <div className="px-6 pb-6 pt-2 border-t border-neutral-100 space-y-4" data-testid="corrosivity-details-panel">
                  <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-5">
                    <Stat label="Temperature" value={result.temperature?.toFixed(1)} unit="°C" />
                    <Stat label="Rel. humidity" value={result.humidity?.toFixed(1)} unit="%" />
                    <Stat label="SO₂ (annual)" value={result.so2_level?.toFixed(2)} unit="mg/m²·d" />
                    <Stat
                      label="Distance to sea"
                      value={
                        Number.isFinite(distanceToSeaKm)
                          ? distanceToSeaKm < 1
                            ? Math.round(distanceToSeaKm * 1000)
                            : distanceToSeaKm.toFixed(1)
                          : '—'
                      }
                      unit={
                        Number.isFinite(distanceToSeaKm)
                          ? distanceToSeaKm < 1 ? 'm' : 'km'
                          : ''
                      }
                    />
                    <Stat label="ISO SO₂ band"       value={result.iso_so2_band} />
                    <Stat label="ISO SO₂ atmosphere" value={result.iso_so2_atmosphere} />
                    <Stat label="Chloride class"     value={result.chloride_class} />
                    <Stat label="Coastline source"   value={result.coastline_data_source} />
                  </div>

                  {calibrationNotes.length > 0 && (
                    <div className="rounded-lg bg-neutral-50 border border-neutral-200 px-4 py-3">
                      <div className="text-[11px] uppercase tracking-wider text-neutral-500 font-semibold mb-2">
                        Calibration notes
                      </div>
                      <ul className="list-disc list-inside space-y-1 text-sm text-neutral-700">
                        {calibrationNotes.map((n, i) => <li key={i}>{n}</li>)}
                      </ul>
                    </div>
                  )}

                  {/* Industrial-site detailed notice — mirrors the top caution */}
                  {isIndustrialSite && (industrialWarning || so2PlaceholderWarn) && (
                    <div className="rounded-lg bg-orange-50 border border-orange-200 px-4 py-3" data-testid="corrosivity-industrial-details">
                      <div className="text-[11px] uppercase tracking-wider text-orange-700 font-semibold mb-2">
                        Industrial facility notice
                      </div>
                      <ul className="list-disc list-inside space-y-1 text-sm text-orange-900">
                        {industrialWarning && <li>{industrialWarning}</li>}
                        {so2PlaceholderWarn && <li>{so2PlaceholderWarn}</li>}
                      </ul>
                    </div>
                  )}

                  {/* Technical / debug section — internal use only.
                      Surfaces the raw resolution metadata so we can debug
                      cases where the result looks unexpected (e.g. Lisbon
                      vs Cádiz routing through different query types). */}
                  <div className="border-t border-neutral-100 pt-3">
                    <button
                      type="button"
                      onClick={() => setShowTechnical((v) => !v)}
                      className="text-xs text-neutral-500 hover:text-neutral-800 inline-flex items-center gap-1.5 font-medium"
                      data-testid="corrosivity-technical-toggle"
                    >
                      <ChevronDown className={`w-3.5 h-3.5 transition-transform ${showTechnical ? 'rotate-180' : ''}`} />
                      {showTechnical ? 'Hide' : 'Show'} technical details
                    </button>
                    {showTechnical && (
                      <div
                        className="mt-3 rounded-lg bg-neutral-900 text-neutral-200 px-4 py-3 text-xs font-mono overflow-x-auto"
                        data-testid="corrosivity-technical-panel"
                      >
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-1.5">
                          <div><span className="text-neutral-500">original_query</span>: {md.original_query ?? '—'}</div>
                          <div><span className="text-neutral-500">original_query_type</span>: <span className="text-emerald-300">{originalQueryType ?? '—'}</span></div>
                          <div><span className="text-neutral-500">resolved_precision</span>: <span className="text-emerald-300">{resolvedPrecision ?? '—'}</span></div>
                          <div><span className="text-neutral-500">result_mode</span>: <span className="text-emerald-300">{resultMode ?? '—'}</span></div>
                          <div><span className="text-neutral-500">coordinate_source</span>: {coordinateSource ?? '—'}</div>
                          <div><span className="text-neutral-500">distance_to_sea_km</span>: {Number.isFinite(distanceToSeaKm) ? distanceToSeaKm.toFixed(3) : '—'}</div>
                          <div><span className="text-neutral-500">chloride_class</span>: {result.chloride_class ?? '—'}</div>
                          <div><span className="text-neutral-500">location / iso_so2_atmosphere</span>: {result.iso_so2_atmosphere ?? '—'}</div>
                          <div><span className="text-neutral-500">settlement_context</span>: {result.settlement_context ?? '—'}</div>
                          <div><span className="text-neutral-500">marine_context</span>: {result.marine_context ?? '—'}</div>
                          <div><span className="text-neutral-500">industrial_override_applied</span>: {String(md.industrial_override_applied ?? false)}</div>
                          <div><span className="text-neutral-500">corrosivity_class</span>: <span className="text-emerald-300">{primary}</span></div>
                          <div><span className="text-neutral-500">precision_level (legacy)</span>: {result.precision_level ?? '—'}</div>
                        </div>
                      </div>
                    )}
                  </div>

                  <div className="text-xs text-neutral-400">
                    Data sources · Open-Meteo Archive (climate) · OpenStreetMap
                    Nominatim with Open-Meteo fallback (geocoding) · Copernicus
                    CAMS (worldwide atmospheric-composition source — CAMS Europe
                    regional air-quality products preferred within Europe; CAMS
                    Global used as default outside Europe) · Natural Earth
                    10&nbsp;m coastline (distance-to-coast).
                  </div>
                </div>
              )}
            </CardContent>
          </Card>

          {/* CTA — detailed probabilistic corrosion study (beyond screening).
              Mirrors the PDF "Beyond screening" block but deliberately avoids
              probabilistic distribution language in this screening view. */}
          <Card className="border-gray-200 bg-gray-50" data-testid="corrosivity-beyond-screening">
            <CardContent className="p-5 space-y-3">
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                <div>
                  <h3 className="text-sm font-semibold text-gray-900">
                    Need a detailed probabilistic corrosion study?
                  </h3>
                  <p className="text-xs text-gray-600 mt-1 leading-relaxed">
                    This application is a screening-level tool intended for
                    early-stage design, feasibility studies and
                    portfolio-scale filtering. For assets with long design
                    lives, high replacement cost, or proximity to aggressive
                    environments, we provide follow-on services including
                    multi-year atmospheric contaminant studies,
                    probabilistic corrosion-rate assessments, and
                    component-specific design and coating recommendations.
                  </p>
                </div>
                <Link to="/engineering/contact" data-testid="corrosivity-cta-request">
                  <Button className="bg-green-600 hover:bg-green-700 text-white text-sm px-5 whitespace-nowrap">
                    Request Full Analysis <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                </Link>
              </div>
              <ul className="text-xs text-gray-600 list-disc list-inside space-y-1 pt-1 border-t border-gray-200">
                <li>Hyperscale data centres — outdoor steel racks, rooftop plant and HVAC coils subject to fine-particulate and SO₂ attack.</li>
                <li>Airports, bridges, port structures and coastal infrastructure with extended design lives and high-consequence failure modes.</li>
                <li>External steelwork, façades and building envelopes in complex urban or industrial microclimates.</li>
                <li>Air-cooling systems and HVAC contamination studies, including ISA-71 gaseous-contamination severity classification.</li>
              </ul>
            </CardContent>
          </Card>

          {/* What does this mean in practice? */}
          <Card className="border-gray-200">
            <CardContent className="pt-5 pb-5 space-y-3" data-testid="corrosivity-practice">
              <h3 className="text-sm font-bold text-gray-800">What does this mean in practice?</h3>
              {(() => {
                const ranges = {
                  C1: "≤ 1.3 µm/year — very low; indoor or clean alpine environments.",
                  C2: "1.3–25 µm/year — low; rural / clean indoor environments.",
                  C3: "25–50 µm/year — medium; urban / coastal inland environments.",
                  C4: "50–80 µm/year — high; coastal-marine and industrial environments.",
                  C5: "80–200 µm/year — very high; coastal splash zones and heavy industrial sites.",
                  CX: "> 200 µm/year — extreme; offshore platforms and tropical coastal zones with continuous splash.",
                };
                const cls = result?.corrosivity_class;
                const r = ranges[cls];
                return (
                  <p className="text-xs text-gray-600 leading-relaxed" data-testid="corrosivity-practice-range">
                    {r ? (
                      <>
                        At corrosivity class <strong>{cls}</strong>, the
                        BS&nbsp;EN&nbsp;ISO&nbsp;9223 first-year metal-loss range
                        for unprotected carbon steel is <strong>{r}</strong>{' '}
                        Even at C3 (medium), unprotected carbon steel loses
                        25–50&nbsp;µm/year — enough to require coating, galvanising
                        or specification of a more resistant alloy for structures
                        with design lives beyond 10–15 years.
                      </>
                    ) : (
                      <>
                        The corrosivity class indicates the typical first-year metal loss
                        expected at this location under open-air exposure. Even at C3
                        (medium), unprotected carbon steel loses 25–50&nbsp;µm/year — enough
                        to require coating, galvanising or specification of a more
                        resistant alloy for structures with design lives beyond 10–15 years.
                      </>
                    )}
                  </p>
                );
              })()}
              <p className="text-xs text-gray-600 leading-relaxed">
                This tool estimates corrosivity from publicly available climate
                and atmospheric data. It does not account for sheltered or
                semi-exposed positions, local industrial point sources, or
                surface-specific accumulation of pollutants. For assets with long
                design lives, proximity to aggressive environments, or high
                replacement cost, site-specific environmental monitoring is
                recommended.
              </p>
              <Link
                to="/engineering/contact"
                className="inline-flex items-center gap-1 text-xs font-semibold text-green-700 hover:underline"
                data-testid="corrosivity-cta-consultation"
              >
                Arrange a consultation <ArrowRight className="w-3 h-3" />
              </Link>
            </CardContent>
          </Card>

          {/* Validity warnings */}
          {Object.values(currentMetals).some((m) => m && (m.validity_warnings || []).length > 0) && (
            <Card className="border-amber-200 bg-amber-50">
              <CardContent className="p-4 text-sm text-amber-900">
                <div className="font-semibold mb-1.5 flex items-center gap-2">
                  <AlertTriangle className="w-4 h-4" /> Validity warnings
                </div>
                <ul className="list-disc list-inside space-y-1 text-amber-800">
                  {Array.from(new Set(
                    Object.values(currentMetals).flatMap((m) => (m && m.validity_warnings) || [])
                  )).map((w, i) => <li key={i}>{w}</li>)}
                </ul>
              </CardContent>
            </Card>
          )}

          {/* Caveats & limitations — moved from sidebar (v26.7).
              Sits below Details & Method, above Disclaimer. Collapsible
              so the page stays scannable for first-time users. */}
          <Card className="border-neutral-200 bg-white" data-testid="corrosivity-caveats-card">
            <CardContent className="pt-5 pb-5">
              <CaveatsAccordion resolvedName={result?.resolved_name} />
            </CardContent>
          </Card>

          {/* Disclaimer */}
          <div className="mt-2 border-t border-neutral-100 pt-6">
            <div className="flex gap-2 text-xs text-neutral-500 leading-relaxed max-w-3xl" data-testid="corrosivity-disclaimer">
              <AlertTriangle className="w-3.5 h-3.5 shrink-0 mt-0.5 text-neutral-400" />
              <p>
                <strong className="text-neutral-600">Disclaimer:</strong> This tool
                provides indicative estimates for preliminary assessment purposes only,
                based on ISO&nbsp;9223:2012 dose-response methodology and the
                environmental datasets consumed by the application (Open-Meteo,
                Copernicus CAMS — Europe regional where available, otherwise CAMS
                Global worldwide; Natural Earth coastline geometry; and OpenStreetMap
                Nominatim geocoding). Results are not a substitute for site-specific
                environmental monitoring or on-site corrosion testing and should not
                be relied upon for detailed design, material specification, or
                construction decisions. Gridded CAMS products may underrepresent
                local pollution conditions at refineries, petrochemical plants,
                steel works, terminals, and similar industrial facilities. Durable
                Designs accepts no liability for any loss or damage arising
                from the use of this tool. For project-specific durability
                verification and corrosion-protection design, independent specialist
                analysis is essential.
              </p>
            </div>
          </div>

          {/* Footer / reference */}
          <div className="text-xs text-neutral-400 max-w-3xl">
            {result.iso_reference}
            {result.from_cache && (
              <span className="ml-2 italic">· Result served from cache ({result.cache_age_h?.toFixed(1)} h old)</span>
            )}
          </div>
        </section>
      )}
    </div>
  );
};

// ── Sd-vs-distance deposition profile chart (v26.6) ─────────────────
// Renders the chloride deposition curve for the resolved location as
// a function of distance to sea, with the actual computed distance
// marked. Shows ISO 9223 chloride bands (S0/S1/S2/S3) as colored
// background. Makes the model's reasoning transparent — users see
// exactly how their classification would shift if they were closer to
// or further from the coast. Pure SVG, no chart library dependency.
const SdProfileChart = ({ profile, waterBody }) => {
  // Hooks must run unconditionally — declare BEFORE any early return.
  const [hoverD, setHoverD] = useState(null);
  const svgRef = useRef(null);

  if (!profile || !Array.isArray(profile.distance_km) || profile.distance_km.length < 2) {
    return null;
  }

  // ── Plot geometry ─────────────────────────────────────────────────
  const W = 720;   // SVG viewbox width
  const H = 280;   // SVG viewbox height
  const M = { top: 18, right: 80, bottom: 42, left: 56 };
  const innerW = W - M.left - M.right;
  const innerH = H - M.top - M.bottom;

  // ── Axis scales ───────────────────────────────────────────────────
  // x: log10 over 0.05 → 50 km. Treat d=0 as 0.05 for plotting.
  const X_MIN = 0.05;
  const X_MAX = 50;
  const xScale = (d) => {
    const dClamped = Math.max(X_MIN, Math.min(X_MAX, d));
    const lx = Math.log10(dClamped);
    const lxMin = Math.log10(X_MIN);
    const lxMax = Math.log10(X_MAX);
    return ((lx - lxMin) / (lxMax - lxMin)) * innerW;
  };
  // y: linear from 0 to a ceiling that includes the curve max AND S2 line.
  const sdValues = profile.Sd_mg_m2_d || [];
  const yCeil = Math.max(60, Math.ceil(Math.max(...sdValues, 60) / 50) * 50);
  const yScale = (sd) => innerH - (Math.max(0, Math.min(yCeil, sd)) / yCeil) * innerH;

  // ── ISO 9223 chloride bands as horizontal background stripes ──────
  // S0: <3, S1: 3-60, S2: 60-300, S3: 300-1500. Render each band as
  // a tinted rect spanning the full x-axis up to its upper boundary.
  const isoBreakpoints = [
    { name: "S0", upper: 3,    fill: "#f0fdf4" /* emerald-50 */ },
    { name: "S1", upper: 60,   fill: "#fefce8" /* yellow-50 */ },
    { name: "S2", upper: 300,  fill: "#fff7ed" /* orange-50 */ },
    { name: "S3", upper: yCeil, fill: "#fef2f2" /* red-50 */ },
  ];

  // ── Curve points (polyline) ───────────────────────────────────────
  const pts = profile.distance_km.map((d, i) => ({
    d,
    sd: sdValues[i] ?? 0,
    x: xScale(d),
    y: yScale(sdValues[i] ?? 0),
  }));
  const polyline = pts.map((p) => `${p.x.toFixed(1)},${p.y.toFixed(1)}`).join(" ");

  // ── Marker for actual location ────────────────────────────────────
  const actualD = profile.actual_distance_km ?? null;
  const actualSd = profile.actual_Sd_mg_m2_d ?? null;
  const markerX = Number.isFinite(actualD) ? xScale(Math.max(X_MIN, actualD)) : null;
  const markerY = Number.isFinite(actualSd) ? yScale(actualSd) : null;

  // ── Tick labels ───────────────────────────────────────────────────
  const xTicks = [0.05, 0.1, 0.25, 0.5, 1, 2, 5, 10, 20, 50];
  const yTickStep = yCeil > 250 ? 50 : 20;
  const yTicks = [];
  for (let v = 0; v <= yCeil; v += yTickStep) yTicks.push(v);

  const fmtD = (d) => {
    if (!Number.isFinite(d)) return "—";
    if (d < 1) return `${Math.round(d * 1000)}m`;
    if (d < 10) return `${d.toFixed(2)}km`;
    return `${Math.round(d)}km`;
  };

  const wbName = waterBody?.name || profile.water_body_name || null;
  const wbFactor = waterBody?.factor ?? profile.water_body_factor ?? null;
  const wbCap = waterBody?.hard_cap_sd ?? profile.hard_cap_sd ?? null;
  const wbType = waterBody?.type;
  const isOpenOcean = !wbType || wbType === "open_ocean";

  // Decide which ISO band the actual point sits in, for the marker label.
  const actualBand = (() => {
    if (!Number.isFinite(actualSd)) return null;
    if (actualSd < 3) return "S0";
    if (actualSd < 60) return "S1";
    if (actualSd < 300) return "S2";
    return "S3";
  })();
  // The actual corrosivity class — sourced from the profile sample
  // closest to the real distance, so the "what-if" badge can be styled
  // differently when it matches vs differs from the authoritative class.
  const actualBandClass = (() => {
    const arr = profile.corrosivity_class;
    if (!Array.isArray(arr) || !arr.length) return null;
    let best = 0;
    let bestGap = Infinity;
    profile.distance_km.forEach((d, i) => {
      const g = Math.abs(d - (actualD ?? 0));
      if (g < bestGap) { bestGap = g; best = i; }
    });
    return arr[best];
  })();

  // ── Interactive "what-if" interpolation ──────────────────────────
  // Given a hover/touch x in SVG coords, invert the log-x scale to a
  // distance in km. Then linearly interpolate Sd, chloride class,
  // corrosivity class and r_corr between the two nearest sample points.
  const lxMin = Math.log10(X_MIN);
  const lxMax = Math.log10(X_MAX);
  const xToDistance = (px) => {
    const ratio = Math.max(0, Math.min(1, px / innerW));
    const lx = lxMin + ratio * (lxMax - lxMin);
    return Math.pow(10, lx);
  };
  const sampleAt = (d) => {
    const ds = profile.distance_km;
    if (d <= ds[0]) return { d: ds[0], sd: sdValues[0], idx: 0, t: 0 };
    if (d >= ds[ds.length - 1]) {
      const i = ds.length - 1;
      return { d: ds[i], sd: sdValues[i], idx: i, t: 1 };
    }
    for (let i = 1; i < ds.length; i++) {
      if (d <= ds[i]) {
        const t = (d - ds[i - 1]) / (ds[i] - ds[i - 1]);
        return {
          d,
          sd: sdValues[i - 1] + t * (sdValues[i] - sdValues[i - 1]),
          idx: i,
          t,
        };
      }
    }
    return { d, sd: 0, idx: ds.length - 1, t: 1 };
  };
  // Interpolated discrete class fields — pick the dominant side of the
  // segment so the class only flips when the hover crosses a real
  // sample boundary. This avoids spurious mid-band classes.
  const cls_arr = profile.corrosivity_class || [];
  const sclass_arr = profile.chloride_class || [];
  const rcorr_arr = profile.r_corr_steel || [];
  const pickAt = (interp, arr) => {
    if (!arr || !arr.length) return null;
    const i = interp.t < 0.5 ? interp.idx - 1 : interp.idx;
    return arr[Math.max(0, Math.min(arr.length - 1, i))];
  };
  const rCorrAt = (interp) => {
    if (!rcorr_arr.length) return null;
    const i = Math.max(0, interp.idx - 1);
    const j = Math.min(rcorr_arr.length - 1, interp.idx);
    return rcorr_arr[i] + (interp.t * (rcorr_arr[j] - rcorr_arr[i]));
  };

  const onPointerMove = (e) => {
    const svg = svgRef.current;
    if (!svg) return;
    const r = svg.getBoundingClientRect();
    const xViewBox = ((e.clientX - r.left) / r.width) * W;
    const innerX = xViewBox - M.left;
    if (innerX < 0 || innerX > innerW) { setHoverD(null); return; }
    setHoverD(xToDistance(innerX));
  };
  const onPointerLeave = () => setHoverD(null);

  const hoverInterp = hoverD !== null ? sampleAt(hoverD) : null;
  const hoverX     = hoverInterp ? xScale(Math.max(X_MIN, hoverInterp.d)) : null;
  const hoverY     = hoverInterp ? yScale(hoverInterp.sd) : null;
  const hoverClass = hoverInterp ? pickAt(hoverInterp, cls_arr) : null;
  const hoverS     = hoverInterp ? pickAt(hoverInterp, sclass_arr) : null;
  const hoverRCorr = hoverInterp ? rCorrAt(hoverInterp) : null;

  return (
    <div
      className="rounded-lg border border-neutral-200 bg-white px-4 py-4"
      data-testid="corrosivity-sd-profile-chart"
    >
      <div className="flex items-start justify-between gap-4 mb-3">
        <div>
          <div className="text-[11px] uppercase tracking-wider text-neutral-500 font-semibold mb-0.5">
            Deposition profile · Sd vs distance to sea
          </div>
          <div className="text-xs text-neutral-600 leading-relaxed">
            How chloride deposition would change if this location were closer to or further from the coast.
            <span className="hidden sm:inline"> Hover or tap the curve to explore.</span>
          </div>
        </div>
      </div>

      <svg
        ref={svgRef}
        viewBox={`0 0 ${W} ${H}`}
        className="w-full h-auto cursor-crosshair touch-none select-none"
        role="img"
        aria-label="Chloride deposition Sd as a function of distance to sea"
        onMouseMove={onPointerMove}
        onMouseLeave={onPointerLeave}
        onTouchMove={(e) => {
          const t = e.touches && e.touches[0];
          if (!t) return;
          onPointerMove({ clientX: t.clientX });
        }}
        onTouchEnd={onPointerLeave}
        data-testid="corrosivity-sd-profile-svg"
      >
        <g transform={`translate(${M.left}, ${M.top})`}>
          {/* ISO band backgrounds */}
          {isoBreakpoints.map((b, i) => {
            const lower = i === 0 ? 0 : isoBreakpoints[i - 1].upper;
            if (lower >= yCeil) return null;
            const upperBound = Math.min(b.upper, yCeil);
            const yTop = yScale(upperBound);
            const yBot = yScale(lower);
            return (
              <rect
                key={b.name}
                x={0}
                y={yTop}
                width={innerW}
                height={Math.max(0, yBot - yTop)}
                fill={b.fill}
              />
            );
          })}

          {/* ISO breakpoint lines */}
          {[3, 60, 300].map((bp) => {
            if (bp >= yCeil) return null;
            return (
              <line
                key={`bp-${bp}`}
                x1={0}
                x2={innerW}
                y1={yScale(bp)}
                y2={yScale(bp)}
                stroke="#cbd5e1"
                strokeWidth={1}
                strokeDasharray="3 3"
              />
            );
          })}

          {/* X-axis grid + ticks */}
          {xTicks.map((d) => (
            <g key={`xt-${d}`}>
              <line
                x1={xScale(d)}
                x2={xScale(d)}
                y1={0}
                y2={innerH}
                stroke="#f1f5f9"
                strokeWidth={1}
              />
              <text
                x={xScale(d)}
                y={innerH + 16}
                fontSize={10}
                fill="#64748b"
                textAnchor="middle"
              >
                {fmtD(d)}
              </text>
            </g>
          ))}

          {/* Y-axis ticks */}
          {yTicks.map((v) => (
            <g key={`yt-${v}`}>
              <text
                x={-8}
                y={yScale(v) + 3}
                fontSize={10}
                fill="#64748b"
                textAnchor="end"
              >
                {v}
              </text>
            </g>
          ))}

          {/* Curve */}
          <polyline
            points={polyline}
            fill="none"
            stroke="#059669"
            strokeWidth={2.4}
            strokeLinejoin="round"
            strokeLinecap="round"
          />

          {/* ISO band labels on right edge */}
          {isoBreakpoints.map((b, i) => {
            const lower = i === 0 ? 0 : isoBreakpoints[i - 1].upper;
            if (lower >= yCeil) return null;
            const upperBound = Math.min(b.upper, yCeil);
            const yMid = (yScale(upperBound) + yScale(lower)) / 2;
            return (
              <text
                key={`lbl-${b.name}`}
                x={innerW + 6}
                y={yMid + 3}
                fontSize={10}
                fill="#64748b"
                textAnchor="start"
                fontWeight={600}
              >
                {b.name}
              </text>
            );
          })}

          {/* Actual location marker */}
          {markerX !== null && markerY !== null && (
            <g>
              <line
                x1={markerX}
                x2={markerX}
                y1={0}
                y2={innerH}
                stroke="#dc2626"
                strokeWidth={1.5}
                strokeDasharray="4 3"
              />
              <circle
                cx={markerX}
                cy={markerY}
                r={5}
                fill="#dc2626"
                stroke="#ffffff"
                strokeWidth={2}
              />
              <g transform={`translate(${markerX + 8}, ${Math.max(14, markerY - 8)})`}>
                <rect
                  x={0} y={-12} width={108} height={26}
                  rx={4} ry={4}
                  fill="#fef2f2"
                  stroke="#fecaca"
                  strokeWidth={1}
                />
                <text x={6} y={0} fontSize={10} fontWeight={700} fill="#991b1b">
                  This location
                </text>
                <text x={6} y={11} fontSize={9} fill="#7f1d1d">
                  {fmtD(actualD)} · Sd={actualSd?.toFixed(1)} · {actualBand}
                </text>
              </g>
            </g>
          )}

          {/* Interactive "what-if" hover marker */}
          {hoverX !== null && hoverY !== null && hoverInterp !== null && (
            <g pointerEvents="none">
              <line
                x1={hoverX}
                x2={hoverX}
                y1={0}
                y2={innerH}
                stroke="#2563eb"
                strokeWidth={1.5}
                strokeDasharray="2 3"
                opacity={0.85}
              />
              <circle
                cx={hoverX}
                cy={hoverY}
                r={5}
                fill="#2563eb"
                stroke="#ffffff"
                strokeWidth={2}
              />
            </g>
          )}

          {/* Y-axis label */}
          <text
            x={-innerH / 2}
            y={-40}
            fontSize={10}
            fill="#475569"
            textAnchor="middle"
            transform="rotate(-90)"
            fontWeight={600}
          >
            Sd (mg·m⁻²·d⁻¹)
          </text>

          {/* X-axis label */}
          <text
            x={innerW / 2}
            y={innerH + 34}
            fontSize={10}
            fill="#475569"
            textAnchor="middle"
            fontWeight={600}
          >
            Straight-line distance to nearest coastline · km (log scale)
          </text>
        </g>
      </svg>

      <div className="mt-2 flex flex-wrap items-center gap-x-4 gap-y-1 text-[11px] text-neutral-600">
        <span className="inline-flex items-center gap-1.5">
          <span className="w-2.5 h-2.5 rounded-sm bg-red-600" /> This location
        </span>
        <span className="inline-flex items-center gap-1.5">
          <span className="w-2.5 h-2.5 rounded-sm bg-blue-600" /> What-if (hover/tap)
        </span>
        <span className="inline-flex items-center gap-1.5">
          <span className="w-2.5 h-0.5 bg-emerald-600 rounded-sm" /> Sd curve
        </span>
        <span className="inline-flex items-center gap-1.5">
          <span className="w-2.5 h-2.5 rounded-sm bg-orange-100 border border-orange-200" />
          ISO 9223 S0/S1/S2/S3 bands
        </span>
      </div>

      {/* v26.9 distance-measurement disclaimer — clarifies that the x-axis
          is a great-circle (geodesic) distance to the nearest coastline
          vertex, NOT a wind-aligned or otherwise directional distance.
          Wind direction enters the model as a separate multiplicative
          factor on Sd, not as a distance transformation. */}
      <div
        className="mt-2 text-[11px] text-neutral-500 leading-relaxed"
        data-testid="corrosivity-sd-profile-disclaimer"
      >
        <strong className="text-neutral-700">How distance is measured —</strong>{' '}
        x-axis values are the shortest great-circle (geodesic) distance from
        the analysed point to the nearest coastline vertex in the Natural
        Earth 10&nbsp;m vector coastline (±100–200&nbsp;m baseline accuracy).
        The distance is non-directional — it does not account for prevailing
        wind, fetch, or whitecap-producing open-ocean orientation. Wind
        direction enters the model as a separate Sd multiplier (Open-Meteo
        annual mean) applied on top of this distance-derived deposition.
      </div>

      {/* What-if interactive readout — fixed slot so layout doesn't jump
          when the user hovers. Inline-pinned actual values on the left,
          interactive values on the right. */}
      <div
        className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-2 text-xs"
        data-testid="corrosivity-sd-profile-readout"
      >
        <div className="rounded-md border border-red-200 bg-red-50 px-3 py-2">
          <div className="text-[10px] uppercase tracking-wider text-red-700 font-semibold">
            This location (authoritative)
          </div>
          <div className="text-neutral-800 mt-0.5">
            <strong>{fmtD(actualD)}</strong> from sea ·
            Sd <strong>{actualSd?.toFixed(1)}</strong> ·
            {" "}<strong>{actualBand}</strong>
          </div>
        </div>
        <div className="rounded-md border border-blue-200 bg-blue-50 px-3 py-2 min-h-[3rem]" data-testid="corrosivity-sd-whatif-readout">
          {hoverInterp ? (
            <>
              <div className="text-[10px] uppercase tracking-wider text-blue-700 font-semibold">
                What-if at this distance
              </div>
              <div className="text-neutral-800 mt-0.5 flex flex-wrap items-baseline gap-x-2 gap-y-0">
                <span>
                  <strong>{fmtD(hoverInterp.d)}</strong> from sea ·
                  Sd <strong>{hoverInterp.sd?.toFixed(1)}</strong> ·
                  {" "}<strong>{hoverS}</strong>
                </span>
                {hoverClass && (
                  <span className="text-[11px]">
                    →
                    <span className={`ml-1 inline-flex items-center px-1.5 py-0.5 rounded font-bold ${
                      hoverClass === actualBandClass
                        ? 'bg-neutral-100 text-neutral-700'
                        : 'bg-blue-600 text-white'
                    }`}>
                      {hoverClass}
                    </span>
                    {Number.isFinite(hoverRCorr) && (
                      <span className="ml-1.5 text-neutral-600">
                        ({hoverRCorr.toFixed(1)} µm/year)
                      </span>
                    )}
                  </span>
                )}
              </div>
            </>
          ) : (
            <div className="text-neutral-500 italic text-[11px] h-full flex items-center">
              Hover or tap the chart to see how class shifts with distance.
            </div>
          )}
        </div>
      </div>

      {/* v26.7 — methodology caveat anchored to the chart itself */}
      <div className="mt-3 rounded-md border border-neutral-200 bg-neutral-50/70 px-3 py-2.5 text-[11px] text-neutral-600 leading-relaxed">
        <strong className="text-neutral-700">How this curve was derived:</strong>{' '}
        the Sd values come from a proprietary distance-decay model
        calibrated to BS&nbsp;EN&nbsp;ISO&nbsp;9223 Annex&nbsp;C and
        Cole&nbsp;et&nbsp;al.&nbsp;(2003), adjusted for the local water-body
        type and surface salinity. The implied corrosivity class is
        computed using the actual location's annual Pd / RH / T from
        Copernicus CAMS, held constant across the distance sweep. This
        is a <strong className="text-neutral-700">visual representation</strong>{' '}
        of the model's response to distance — not a substitute for proper
        BS&nbsp;EN&nbsp;ISO&nbsp;9225 wet-candle salinity measurement or
        12-month BS&nbsp;EN&nbsp;ISO&nbsp;9223 corrosion-coupon exposure
        testing, which remain the authoritative methods for engineering
        specification.
      </div>
    </div>
  );
};

// ── Collapsible "Caveats & limitations" sidebar card ───────────────
// Hidden behind a click so the sidebar stays uncluttered for the
// 80 % of visitors who just want the headline result. Engineers who
// need to defend the number can expand it for the full disclosure.
const CaveatsAccordion = ({ resolvedName }) => {
  const [open, setOpen] = useState(false);
  // v26.7: DMRB CD 361 only applies in the UK — gate the caveat
  // on the resolved location being inside the UK.
  const isUk = (() => {
    const s = (resolvedName || "").toLowerCase();
    return /(united kingdom|england|scotland|wales|northern ireland|isle of man|cymru|alba)/.test(s);
  })();
  return (
    <div data-testid="corrosivity-caveats-accordion">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="w-full flex items-center justify-between gap-2 text-left text-sm font-bold text-gray-800 hover:text-emerald-700 transition-colors"
        aria-expanded={open}
        data-testid="corrosivity-caveats-toggle"
      >
        <span>Caveats &amp; limitations</span>
        <ChevronDown
          className={`w-4 h-4 flex-shrink-0 transition-transform ${open ? 'rotate-180' : ''}`}
        />
      </button>
      {!open && (
        <p className="text-xs text-gray-500 mt-1.5 leading-relaxed">
          What the model can and can't see — click to expand.
        </p>
      )}
      {open && (
        <ul
          className="mt-3 space-y-2.5 text-xs text-gray-600 leading-relaxed"
          data-testid="corrosivity-caveats-list"
        >
          <li>
            <strong className="text-gray-700">Screening-grade, not a substitute for testing</strong>{' '}
            — this tool produces a model-based estimate intended for early-stage
            design, feasibility studies and portfolio screening. It does not
            replace BS EN ISO 9225 wet-candle salinity monitoring or 12-month
            BS EN ISO 9223 corrosion-coupon exposure testing, which are the
            authoritative methods for engineering specification.
          </li>
          {isUk && (
            <li>
              <strong className="text-gray-700">Weathering-steel bridges in the UK &lt; 15 km of coast</strong>{' '}
              — DMRB CD 361 mandates full ISO 9225 12-month salinity testing
              and ISO 9223 corrosivity assessment for any structure within
              15&nbsp;km of the UK coast. This tool is useful for early
              screening of suitability and to inform whether project
              programmes can accommodate the mandated testing window, but
              cannot itself satisfy that requirement. (See SCI Steel Bridge
              Group / Arup study, Sangster, Frydman &amp; Gedge, 2023.)
            </li>
          )}
          <li>
            <strong className="text-gray-700">ISO 9223 dose-response uncertainty</strong>{' '}
            — BS EN ISO 9223:2012 states the dose-response function carries
            a stated uncertainty band of −33&nbsp;% / +50&nbsp;% for carbon
            steel, zinc and copper, and −50&nbsp;% / +100&nbsp;% for aluminium.
            Treat the µm/year figure as an order-of-magnitude guide, not a
            precision measurement. The corrosivity class is more robust to
            this uncertainty than the rate.
          </li>
          <li>
            <strong className="text-gray-700">CAMS grid resolution</strong>{' '}
            — Copernicus EAC4 reanalysis (Europe regional) runs at 0.1° (~10 km)
            and CAMS Global at 0.75° (~80&nbsp;km); the WOA23 salinity correction
            at ~0.25° (~28&nbsp;km). Within-city micro-climates, port basins, and the
            lee/windward sides of headlands are smaller than these cells and won't
            be fully distinguished.
          </li>
          <li>
            <strong className="text-gray-700">Coastline data resolution (±100–200 m)</strong>{' '}
            — distance to sea is derived from Natural Earth 10m vector
            coastline, which has a baseline accuracy of ±100–200 m and may
            include small tidal inlets / sheltered coves as marine water.
            For coastal sites near narrow inlets (Tintagel Haven, Bossiney
            Cove, etc.) the reported distance may under-state the distance
            to the open whitecap-producing ocean — though the corrosivity
            class is robust to this error because cliff splash dominates
            within ~1 km regardless.
          </li>
          <li>
            <strong className="text-gray-700">Wind direction is a modifying factor only</strong>{' '}
            — prevailing wind direction modifies the chloride exposure
            estimate (sites with onshore winds blowing from the open ocean
            see somewhat higher Sd than leeward sites at the same
            distance). The effect is bounded and applied on top of the
            ISO 9223 dose-response; it does not override the base
            classification.
          </li>
          <li>
            <strong className="text-gray-700">SO₂ is regional background, not point sources</strong>{' '}
            — CAMS reports the broad atmospheric mean, so a site directly downwind
            of a refinery, port, or coal plant will be under-estimated. Following
            the SCI/Arup study convention, sites near ports, harbours and
            industrial facilities should conservatively assume P1 (upper-bound
            urban). Use the
            <em> "Treat this as an industrial facility / service atmosphere"</em>
            {' '}toggle to flag these cases; it adds an engineering caution to the
            report.
          </li>
          <li>
            <strong className="text-gray-700">Wind-direction weighting is partial</strong>{' '}
            — the Open-Meteo wind multiplier scales Sd up for sites whose dominant
            wind blows onshore from the open ocean and down for leeward sites, but
            it's a single annual mean — short, intense storm events and topographic
            funnelling are not resolved.
          </li>
          <li>
            <strong className="text-gray-700">No microclimate, orientation or TOW modelling</strong>{' '}
            — sheltered vs. exposed elevations, height above ground, indoor
            environments, and time-of-wetness on the actual structure surface
            are outside the model. For critical assets, pair the headline class
            with on-site coupon exposure or LPR monitoring.
          </li>
          <li>
            <strong className="text-gray-700">Historical comparison is two reference years</strong>{' '}
            — the 2013 → current comparison flags directional change in the local
            atmosphere; it is not a continuous multi-decadal trend.
          </li>
          <li>
            <strong className="text-gray-700">Inland salt-affected zones (de-icing salt) — not modelled</strong>{' '}
            — winter road and runway de-icing applies high local chloride loads
            well inland, particularly along motorways, on bridge decks and around
            airports. The current model only sees marine chloride. An ingestion
            path for transport-authority salting records is on the backlog.
          </li>
          <li>
            <strong className="text-gray-700">Alignment with SCI / Arup 2023 study</strong>{' '}
            — for UK coastal sites the model is broadly consistent with the
            findings of Sangster, Frydman &amp; Gedge (2023) for the SCI Steel
            Bridge Group: C5 only within &lt;~2&nbsp;km of the coast (often &lt;1&nbsp;km),
            transitioning to C4 / C3 by 2–5&nbsp;km inland, in line with the
            EUR&nbsp;7433 and ISOCORRAG datasets that underpin BS EN ISO 9223
            Annex C. The published study used wet-candle Sd measurements
            (ISO 9225); this tool substitutes modelled CAMS sea-salt
            deposition, which is a less precise but globally-applicable
            input.
          </li>
        </ul>
      )}
    </div>
  );
};

export default CorrosivityCalculator;

